import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { localEventDate } from '../../shared/eventTimezones';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  ListSubheader,
  Menu,
  MenuItem,
  IconButton,
  Paper,
  Popover,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GroupsIcon from '@mui/icons-material/Groups';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import CircleIcon from '@mui/icons-material/Circle';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { closestCorners, DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors, type CollisionDetection, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { usePersistentScrollRestoration } from '../hooks/usePersistentViewState';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import { lookupLabel } from '../data/lookups';
import { lineupStatusOptions, participationCancellationTypeOptions, participationEventReasonOptions } from '../data/options';
import type {
  AvailabilityStatus,
  LeagueConfigurationPositionRecord,
  LineupEntryRecord,
  LineupStatus,
  LookupState,
  MatchEventRecord,
  MatchSiteType,
  ParticipationCancellationType,
  ParticipationEventReason,
  PlayerRatingBasis,
  PlayerNameDisplayFormat,
  PlayerSortOrder,
  StandbyPlayerRecord,
  TimeFormat,
  UserPreferences,
} from '../types';
import { formatPlayerFirstLast, formatPlayerName } from '../utils/playerPreferences';
import { compareLineupSelectablePlayers, isLineupSelectablePlayer, lineupTierKey } from '../utils/lineupPlayerSort';
import { lookupTeamAccentColor } from '../utils/teamColors';
import { formatTime } from '../utils/timeFormat';
import { effectiveLineLogistics, matchHasSplitSchedule, type LineLogisticsOverrides } from '../utils/lineLogistics';
import {
  compatibilityChipColor,
  normalizeCompatibility,
  normalizePairingRule,
  pairingRuleChipColor,
  pairingRuleHelpText,
  pairingRuleLabels,
  partnerPreferenceForPair,
  type PartnerCompatibility,
  type PartnerPairingRule,
} from '../utils/partnerPreferences';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import { GroupMeLineupSend } from './GroupMeLineupSend';
import {
  calculateEdrDifference,
  calculateDoublesLineAvgEdr,
  calculateLineupAverageEdr,
  formatEdrDifference,
  formatEdr,
  hasEdr,
} from '../utils/edr';
import {
  buildLineupHealth,
  fallbackLineupHealthSummary,
  type LineupHealthMetric,
  type LineupHealthSeverity,
} from '../utils/lineupHealth';
import { buildBreadcrumbTrail, lineupCanonicalContext, type BreadcrumbItem } from '../utils/uxNavigation';
import { loadParticipationReportRows, type ParticipationReportRow } from '../utils/participationReport';
import {
  defaultStrategyForAdvancementType,
  generateLineupRecommendationAnalysis,
  type AdvancementType,
  type RecommendationAnalysis,
  type RecommendationPlan,
  type RecommendationStrategyProfile,
  type SharedPlayerScheduleAssignment,
  type SinglesPreference,
  normalizeMultiTeamSchedulingConfig,
} from '../utils/lineupRecommendationEngine';
import { isDesignatedSubstitute, normalizeRosterRole, type RosterRole } from '../utils/rosterRoles';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';
import { ContextBackButton } from './ux/ContextBackButton';
import { groupWorkspacePoolPlayersByGender, lineupTileMetrics, optimisticWorkspaceMove, sortWorkspacePoolPlayers } from '../utils/lineupWorkspace';
import { applyTeamRecommendationSettings, type TeamRecommendationSettings } from '../utils/teamRecommendationSettings';

type LineupBuilderProps = {
  matchId: string | null;
  lookups: LookupState;
  onBackToMatch: (matchId: string) => void;
  onOpenAvailability?: (matchId: string, teamId: string) => void;
  onOpenMatchDetail?: (matchId: string) => void;
  onOpenTeamEvents?: () => void;
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  timeFormat: TimeFormat;
  readOnly?: boolean;
  canViewPartnerPreferences?: boolean;
  breadcrumbOrigin?: 'team-events' | 'planning-board';
  isWorkflowFullScreen?: boolean;
  onExitWorkflowFullScreen?: () => void;
};

type RosterPlayer = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  sex: 'male' | 'female';
  ntrp_rating: number | null;
  estimated_dynamic_rating: number | null;
  utr_singles_rating: number | null;
  utr_doubles_rating: number | null;
  singles_preference: SinglesPreference;
  player_tier: string | null;
  roster_status: string;
  roster_role: RosterRole | string | null;
  target_match_count: number | null;
  availability_status: AvailabilityStatus;
  completed_played: number;
  completed_singles: number;
  completed_doubles: number;
  future_singles: number;
  future_doubles: number;
  /** Non-completed assignments for this team and season, excluding this match. */
  unplayed_non_completed: number;
  /** Canonical participation status delta from the shared team-season report. */
  participation_status_delta?: number;
  back_to_back_match_day_tolerance: 'no' | 'if_needed' | 'yes' | null;
  same_day_match_tolerance: 'no' | 'if_needed' | 'yes' | null;
  minimum_recovery_hours: number | null;
  team_priority: 'primary' | 'normal' | 'secondary' | null;
};

type RosterRow = {
  player_id: string;
  player_tier: string | null;
  roster_status: string;
  roster_role: RosterRole | string | null;
  target_match_count: number | null;
  team_priority: 'primary' | 'normal' | 'secondary' | null;
};

type PlayerRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  sex: 'male' | 'female';
  ntrp_rating: number | null;
  estimated_dynamic_rating: number | null;
  utr_singles_rating: number | null;
  utr_doubles_rating: number | null;
  singles_preference: SinglesPreference;
  back_to_back_match_day_tolerance: 'no' | 'if_needed' | 'yes' | null;
  same_day_match_tolerance: 'no' | 'if_needed' | 'yes' | null;
  minimum_recovery_hours: number | null;
};

type ConflictRow = {
  player_id: string;
  match_event_id: string;
  opponent_name: string | null;
  team_id: string;
  position_code: string | null;
};

type LineStatus = 'normal' | 'default_by_opponent' | 'default_by_us';

type LineStatusRecord = {
  id?: string;
  match_event_id: string;
  league_configuration_position_id: string;
  line_status: LineStatus;
} & LineLogisticsOverrides;

type WorkloadCounts = {
  completed_played: number;
  completed_singles: number;
  completed_doubles: number;
  future_singles: number;
  future_doubles: number;
  unplayed_non_completed: number;
};

type PartnerPreferenceRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  player1_id: string;
  player2_id: string;
  compatibility: PartnerCompatibility | null;
  pairing_rule: PartnerPairingRule | null;
  is_active?: boolean | null;
  notes: string | null;
};

type PairingRuleOverrideRow = {
  id: string;
  match_event_id: string;
  partner_preference_id: string;
  pairing_rule: PartnerPairingRule;
  override_reason: string;
  created_by: string | null;
  created_at: string;
};

type MatchLifecycleHistoryRow = {
  id: string;
  match_event_id: string;
  previous_lifecycle_state: string | null;
  new_lifecycle_state: string;
  changed_at: string;
  changed_by: string | null;
  change_reason: string | null;
};

type TeamStrategyRow = TeamRecommendationSettings & {
  advancement_type?: AdvancementType | null;
  recommendation_strategy_profile_id?: string | null;
};

type StrategyProfileRow = {
  id: string;
  name: string;
  advancement_type: AdvancementType;
  competitiveness_weight: number;
  participation_fairness_weight: number;
  singles_opportunity_weight: number;
  schedule_spacing_weight: number;
  partner_compatibility_weight: number;
  home_away_balance_weight: number;
  singles_edr_weight: number;
  singles_utr_weight: number;
  doubles_edr_weight: number;
  doubles_utr_weight: number;
  change_sensitivity: 'flexible' | 'moderate' | 'conservative';
  tentative_player_policy: 'exclude_tentative' | 'standby_only' | 'allow_in_draft_with_warning';
  preferred_pair_bonus?: number;
  preferred_pair_separation_penalty?: number;
  strongly_preferred_pair_bonus?: number;
  strongly_preferred_pair_separation_penalty?: number;
  avoid_pairing_penalty?: number;
  poor_compatibility_penalty?: number;
  good_compatibility_bonus?: number;
  excellent_compatibility_bonus?: number;
  multi_team_scheduling_config?: unknown;
  enforce_target_matches?: boolean | null;
  target_match_variance?: number | null;
  enforce_max_sit?: boolean | null;
  max_weeks_without_match?: number | null;
  use_partner_compatibility?: boolean | null;
  use_partner_scheduling_preferences?: boolean | null;
  version: number;
};

type Slot = {
  position: LeagueConfigurationPositionRecord;
  slotNumber: number;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};

type CancellationForm = {
  entryId: string;
  cancellationType: ParticipationCancellationType;
  reason: ParticipationEventReason;
  replacementPlayerId: string;
  notes: string;
  cancellationCountsTowardAllocation: boolean;
  manualAllocationOverride: boolean;
  replacementAllocationOverride: boolean;
};
type LineupLocalDraft = {
  standbyPlayerId: string;
  standbyNotes: string;
  cancellationForm: CancellationForm;
};

type ParticipationEventPayload = {
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string;
  participation_event_type: string;
  related_player_participation_event_id?: string | null;
  allocation_count_indicator: boolean;
  manual_allocation_override_indicator?: boolean;
  participation_event_reason: string;
  participation_event_notes: string | null;
  replaced_player_id?: string | null;
  replacement_player_id?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
};

const tierLabels: Record<string, string> = {
  tier_1: 'Tier 1 - stronger players',
  tier_2: 'Tier 2 - middle players',
  tier_3: 'Tier 3 - developing players',
  tier_4: 'Tier 3 - developing players',
};

const tierOrder = ['tier_1', 'tier_2', 'tier_3', 'tier_4', ''] as const;

const availabilityLabels: Record<AvailabilityStatus, string> = {
  available: 'Available',
  maybe: 'Maybe',
  unknown: 'Unknown',
  not_available: 'Not Available',
};

const lineStatusOptions: Array<{ value: LineStatus; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'default_by_opponent', label: 'Default by Opponent' },
  { value: 'default_by_us', label: 'Default by Us' },
];

const defaultedLineStatuses = new Set<LineStatus>(['default_by_opponent', 'default_by_us']);

const healthSeverityColor: Record<LineupHealthSeverity, 'success' | 'info' | 'warning' | 'error'> = {
  positive: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

function healthSeverityIcon(severity: LineupHealthSeverity) {
  if (severity === 'positive') return <CheckCircleOutlineIcon fontSize="small" />;
  if (severity === 'info') return <InfoOutlinedIcon fontSize="small" />;
  return <ErrorOutlineIcon fontSize="small" />;
}

function severityForHealthScore(score: number): LineupHealthSeverity {
  if (score >= 90) return 'positive';
  if (score >= 75) return 'info';
  if (score >= 60) return 'warning';
  return 'error';
}

function playerName(player: Pick<RosterPlayer, 'first_name' | 'last_name'> | undefined, preferences: UserPreferences): string {
  return formatPlayerName(player, preferences);
}

function playerDisplay(player: RosterPlayer, preferences: UserPreferences): string {
  const sex = player.sex === 'male' ? 'M' : 'F';
  const ntrp = typeof player.ntrp_rating === 'number' ? player.ntrp_rating.toFixed(1) : 'NTRP -';
  const tier = tierLabel(player.player_tier);
  return `${playerName(player, preferences)} (${[sex, ntrp, `EDR ${formatEdr(player.estimated_dynamic_rating)}`, tier, availabilityLabels[player.availability_status]].filter(Boolean).join(', ')})`;
}

function playerWorkloadSummary(player: RosterPlayer): string {
  if (isDesignatedSubstitute(player)) return 'Designated substitute • no target match count';
  const metrics = lineupTileMetricsForPlayer(player, false);
  const delta = metrics.targetDelta === null ? '—' : `${metrics.targetDelta > 0 ? '+' : ''}${metrics.targetDelta}`;
  return `Played ${metrics.played} • Unplayed ${metrics.unplayed} • Δ ${delta}`;
}

function lineupTileMetricsForPlayer(player: RosterPlayer, currentMatchAssigned: boolean) {
  const fallback = lineupTileMetrics({
    completedPlayed: player.completed_played,
    nonCompletedAssignments: player.unplayed_non_completed,
    currentMatchAssigned,
    target: player.target_match_count,
  });
  if (typeof player.participation_status_delta !== 'number') return fallback;
  return {
    played: player.completed_played,
    unplayed: player.unplayed_non_completed,
    targetDelta: player.participation_status_delta,
  };
}

function tierLabel(playerTier: string | null): string {
  return tierLabels[playerTier || ''] || '';
}

function rosterGroupLabel(playerTier: string): string {
  return tierLabel(playerTier) || 'Roster';
}

function availabilityChipColor(status: AvailabilityStatus): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'available') return 'success';
  if (status === 'maybe') return 'warning';
  if (status === 'not_available') return 'error';
  return 'default';
}

function compatibilitySummaryChipColor(value?: string | null): 'success' | 'info' | 'default' | 'warning' | 'error' {
  const compatibility = normalizeCompatibility(value);
  if (compatibility === 'Excellent') return 'success';
  if (compatibility === 'Good') return 'info';
  if (compatibility === 'Poor') return 'warning';
  return 'default';
}

function pairingRuleIsHard(value?: string | null): boolean {
  return ['required_when_both_selected', 'schedule_only_as_pair', 'excluded'].includes(normalizePairingRule(value));
}

function pairingRuleIsSoft(value?: string | null): boolean {
  return ['preferred', 'strongly_preferred', 'avoid_pairing'].includes(normalizePairingRule(value));
}

function playerRatingSummary(player: RosterPlayer, lineType: string | null): string {
  const utr = lineType === 'singles' ? player.utr_singles_rating : player.utr_doubles_rating;
  const utrLabel = lineType === 'singles' ? 'Singles UTR' : 'Doubles UTR';
  return `EDR ${formatEdr(player.estimated_dynamic_rating)} • ${utrLabel} ${typeof utr === 'number' ? utr.toFixed(2) : '-'}`;
}

function lineDisplayName(position: LeagueConfigurationPositionRecord): string {
  const match = position.position_code.match(/^([A-Za-z]+)\s*(\d+)$/);
  const lineNumber = match?.[2] || position.position_code.replace(/\D/g, '');
  if (position.line_type === 'singles') return `Singles ${lineNumber || position.position_code}`;
  if (position.line_type === 'doubles') return `Doubles ${lineNumber || position.position_code}`;
  return position.display_name || position.position_code;
}

function lineStatusLabel(lineStatus: LineStatus): string {
  return lineStatusOptions.find((option) => option.value === lineStatus)?.label || 'Normal';
}

function lineEdrLabel(playersForLine: Array<RosterPlayer | undefined>, lineType: string | null): string {
  if (lineType === 'singles') {
    return `EDR ${formatEdr(playersForLine[0]?.estimated_dynamic_rating)}`;
  }
  const averageEdr = calculateDoublesLineAvgEdr(playersForLine[0], playersForLine[1]);
  const assignedPlayers = playersForLine.filter(Boolean);
  const missingCount = assignedPlayers.filter((player) => !hasEdr(player?.estimated_dynamic_rating)).length;
  return `Avg EDR ${formatEdr(averageEdr)}${averageEdr !== null && missingCount > 0 ? '*' : ''}`;
}

function groupedRosterPlayers(players: RosterPlayer[], preferences: UserPreferences): Array<{ tier: string; label: string; players: RosterPlayer[] }> {
  const regularGroups = tierOrder
    .map((tier) => ({
      tier,
      label: rosterGroupLabel(tier),
      players: players
        .filter((player) => isLineupSelectablePlayer(player) && !isDesignatedSubstitute(player) && lineupTierKey(player.player_tier) === tier)
        .sort((a, b) => compareLineupSelectablePlayers(a, b, preferences)),
    }))
    .filter((group) => group.players.length > 0);
  const substitutes = players
    .filter((player) => isLineupSelectablePlayer(player) && isDesignatedSubstitute(player))
    .sort((a, b) => compareLineupSelectablePlayers(a, b, preferences));
  return substitutes.length > 0
    ? [...regularGroups, { tier: 'designated_substitute', label: 'Designated Substitutes', players: substitutes }]
    : regularGroups;
}

function formatMatch(match: MatchEventRecord | null, lookups: LookupState, timeFormat: TimeFormat): string {
  if (!match) return '';
  const time = formatTime(match.match_time, timeFormat, match);
  return `${lookupLabel(lookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'} on ${localEventDate(match)}${time ? ` at ${time}` : ''}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function slotKey(positionId: string, slotNumber: number): string {
  return `${positionId}:${slotNumber}`;
}

type WorkspaceTileProps = {
  player: RosterPlayer;
  ratingBasis: PlayerRatingBasis;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  currentMatchAssigned?: boolean;
  activePartnerContext?: boolean;
  signals?: WorkspaceTileSignal[];
};

type WorkspaceTileSignalKind = 'do_not_partner' | 'preferred_partner' | 'compatible_partner' | 'target' | 'participation' | 'workload' | 'maybe_assignment';

type WorkspaceTileSignal = {
  kind: WorkspaceTileSignalKind;
  title: string;
  detail: string;
  priority: number;
};

type ActivePartnerContext = {
  playerId: string;
  positionId: string;
};

const workspacePlayerTileWidth = 220;
// Pointer drops must land inside a real slot or the pool. Nearest-corner
// fallback is reserved for keyboard dragging, where there is no pointer.
const workspaceCollisionDetection: CollisionDetection = (args) =>
  args.pointerCoordinates ? pointerWithin(args) : closestCorners(args);
const workspacePlayerTileHeight = 116;
const workspacePlayerTileCompactHeight = 108;

const workspacePlayerTileGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: `repeat(auto-fit, minmax(min(100%, ${workspacePlayerTileWidth}px), ${workspacePlayerTileWidth}px))`,
  },
  gap: 1,
  justifyContent: 'start',
} as const;

function availabilityDotColor(status: AvailabilityStatus): string {
  if (status === 'available') return 'success.main';
  if (status === 'maybe') return 'warning.main';
  if (status === 'not_available') return 'error.main';
  return 'grey.500';
}

function signalIcon(kind: WorkspaceTileSignalKind) {
  if (kind === 'do_not_partner') return <BlockIcon fontSize="inherit" />;
  if (kind === 'preferred_partner') return <GroupsIcon fontSize="inherit" />;
  if (kind === 'compatible_partner') return <HandshakeIcon fontSize="inherit" />;
  if (kind === 'target') return <TrackChangesIcon fontSize="inherit" />;
  if (kind === 'participation') return <EventBusyIcon fontSize="inherit" />;
  if (kind === 'workload') return <SwapHorizIcon fontSize="inherit" />;
  return <WarningAmberIcon fontSize="inherit" />;
}

function WorkspacePlayerTile({ player, ratingBasis, selected = false, disabled = false, onClick, currentMatchAssigned = false, activePartnerContext = false, signals = [] }: WorkspaceTileProps) {
  const metrics = lineupTileMetricsForPlayer(player, currentMatchAssigned);
  const tierNumber = String(player.player_tier || '').match(/([1-4])/u)?.[1] || '—';
  const rating = ratingBasis === 'tier' ? `Tier ${tierNumber}` : `EDR ${formatEdr(player.estimated_dynamic_rating)}`;
  const genderLabel = player.sex === 'female' ? 'Woman' : 'Man';
  const signalText = signals.map((signal) => signal.title).join('. ');
  const visibleSignals = signals.slice(0, 3);
  const overflowSignals = signals.slice(3);
  return (
    <Paper
      variant="outlined"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-label={`${player.first_name} ${player.last_name}, ${genderLabel}. Availability ${availabilityLabels[player.availability_status]}. ${rating}. Played ${metrics.played}. Unplayed ${metrics.unplayed}. Target Delta ${metrics.targetDelta === null ? 'not set' : metrics.targetDelta}. ${activePartnerContext ? 'Active partner context. ' : ''}${signalText}`}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (onClick && !disabled && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        // Gender shading is consistent across every team format; eligibility
        // remains enforced by the canonical slot rules, never by tile color.
        bgcolor: player.sex === 'female' ? 'rgba(220, 90, 135, 0.09)' : 'rgba(48, 119, 181, 0.09)',
        borderWidth: selected || activePartnerContext ? 2 : 1,
        borderColor: activePartnerContext || selected ? 'primary.main' : 'divider',
        boxShadow: activePartnerContext ? '0 0 0 1px rgba(25, 118, 210, 0.16)' : undefined,
        opacity: disabled ? 0.58 : 1,
        cursor: onClick && !disabled ? 'pointer' : 'grab',
        p: 1,
        width: '100%',
        minWidth: 0,
        height: { xs: workspacePlayerTileCompactHeight, sm: workspacePlayerTileHeight },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2 },
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="space-between" sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.35} alignItems="center" sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: '1 1 auto', fontWeight: 800, lineHeight: 1.2 }}>{player.first_name} {player.last_name}</Typography>
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flex: '0 1 auto', minWidth: 0, color: 'text.secondary', fontSize: 15, overflow: 'hidden' }}>
            {visibleSignals.map((signal) => (
              <Tooltip key={signal.kind} title={<><strong>{signal.title}</strong><br />{signal.detail}</>} arrow enterTouchDelay={0}>
                <Box component="span" tabIndex={0} aria-label={`${signal.title}: ${signal.detail}`} sx={{ display: 'inline-flex', flex: '0 0 auto', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', borderRadius: 0.5 } }}>
                  {signalIcon(signal.kind)}
                </Box>
              </Tooltip>
            ))}
            {overflowSignals.length > 0 && (
              <Tooltip title={overflowSignals.map((signal) => `${signal.title}: ${signal.detail}`).join('\n')} arrow enterTouchDelay={0}>
                <Box component="span" tabIndex={0} aria-label={`${overflowSignals.length} additional planning signals`} sx={{ fontSize: '0.65rem', fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', borderRadius: 0.5 } }}>
                  +{overflowSignals.length}
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Tooltip title={`Availability: ${availabilityLabels[player.availability_status]}`} arrow enterTouchDelay={0}>
          <Box component="span" tabIndex={0} aria-label={`Availability: ${availabilityLabels[player.availability_status]}`} sx={{ display: 'inline-flex', lineHeight: 1, color: availabilityDotColor(player.availability_status), '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', borderRadius: '50%' } }}>
            <CircleIcon sx={{ fontSize: 18 }} />
          </Box>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2 }}>{rating}</Typography>
      <Box sx={{ mt: 'auto', display: 'grid', gridTemplateColumns: 'minmax(2.25ch, auto) minmax(0, 1fr)', columnGap: 0.75, rowGap: 0.1, alignItems: 'baseline', minWidth: 0 }}>
        <Typography variant="caption" sx={{ minWidth: '2.25ch', textAlign: 'right', fontWeight: 800 }}>{metrics.played}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>Played</Typography>
        <Typography variant="caption" sx={{ minWidth: '2.25ch', textAlign: 'right', fontWeight: 800 }}>{metrics.unplayed}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>Unplayed</Typography>
        <Tooltip title="Difference between this player's current total assignments and Target Matches for this team and season. Negative is below target; positive is above target." arrow enterTouchDelay={0}>
          <Box component="span" tabIndex={0} sx={{ minWidth: '2.25ch', textAlign: 'right', fontWeight: 800, color: metrics.targetDelta === null || metrics.targetDelta === 0 ? 'text.primary' : metrics.targetDelta > 0 ? 'success.main' : 'error.main', outlineOffset: 2, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light' } }}>
            <Typography variant="caption" component="span" sx={{ fontWeight: 'inherit', color: 'inherit' }}>{metrics.targetDelta === null ? '—' : `${metrics.targetDelta > 0 ? '+' : ''}${metrics.targetDelta}`}</Typography>
          </Box>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>Target Δ</Typography>
      </Box>
    </Paper>
  );
}

function DraggableWorkspacePlayerTile(props: WorkspaceTileProps & { dragId: string; playerId: string }) {
  const { dragId, playerId, onClick, disabled, ...tileProps } = props;
  const dragData = useMemo(() => ({ playerId }), [playerId]);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: dragId, data: dragData, disabled: Boolean(disabled) });
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={disabled ? undefined : onClick}
      sx={{ touchAction: 'none', opacity: isDragging ? 0 : 1, transform: CSS.Translate.toString(transform) }}
    >
      <WorkspacePlayerTile {...tileProps} disabled={disabled} />
    </Box>
  );
}

function WorkspaceSlot({
  slot,
  player,
  isMixed,
  isDefaulted,
  canEdit,
  ratingBasis,
  onSelect,
  onRemove,
  draggingPlayer,
  selectedPoolPlayerName,
  activePartnerContext,
  signals,
}: {
  slot: Slot;
  player?: RosterPlayer;
  isMixed: boolean;
  isDefaulted: boolean;
  canEdit: boolean;
  ratingBasis: PlayerRatingBasis;
  onSelect: () => void;
  onRemove?: () => void;
  draggingPlayer?: RosterPlayer;
  selectedPoolPlayerName?: string | null;
  activePartnerContext?: boolean;
  signals?: WorkspaceTileSignal[];
}) {
  const id = `slot:${slot.position.id}:${slot.slotNumber}`;
  const genderTarget = isMixed && slot.position.required_player_count === 2 ? (slot.slotNumber === 1 ? 'female' : 'male') : null;
  const invalidGenderTarget = Boolean(draggingPlayer && genderTarget && draggingPlayer.sex !== genderTarget);
  const dropData = useMemo(
    () => ({ positionId: slot.position.id, slotNumber: slot.slotNumber, genderTarget }),
    [genderTarget, slot.position.id, slot.slotNumber],
  );
  const { isOver, setNodeRef } = useDroppable({ id, disabled: !canEdit || isDefaulted || invalidGenderTarget, data: dropData });
  if (isDefaulted) return <Paper variant="outlined" sx={{ minHeight: { xs: workspacePlayerTileCompactHeight, sm: workspacePlayerTileHeight }, p: 1.25, bgcolor: 'background.default' }}><Typography variant="body2" color="text.secondary">No player assignment required.</Typography></Paper>;
  if (player) {
    return (
      <Box ref={setNodeRef} data-lineup-slot={id} sx={{ position: 'relative', borderRadius: 1, outline: isOver || invalidGenderTarget ? '3px solid' : undefined, outlineColor: invalidGenderTarget ? 'error.light' : isOver ? 'primary.light' : undefined }}>
        <DraggableWorkspacePlayerTile
          dragId={`slot-player:${slot.position.id}:${slot.slotNumber}:${player.id}`}
          playerId={player.id}
          player={player}
          ratingBasis={ratingBasis}
          currentMatchAssigned
          activePartnerContext={activePartnerContext}
          signals={signals}
          disabled={!canEdit}
          onClick={onSelect}
        />
        {canEdit && onRemove && (
          <Tooltip title="Remove from lineup">
            <IconButton aria-label={`Remove ${player.first_name} ${player.last_name} from lineup`} size="small" onClick={(event) => { event.stopPropagation(); onRemove(); }} sx={{ position: 'absolute', right: 4, bottom: 4, bgcolor: 'background.paper' }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }
  const label = genderTarget === 'female' ? 'Drop Woman Here' : genderTarget === 'male' ? 'Drop Man Here' : 'Drop Player Here';
  const canAssignSelectedPlayer = Boolean(selectedPoolPlayerName && !invalidGenderTarget);
  return (
    <Paper
      ref={setNodeRef}
      data-lineup-slot={id}
      variant="outlined"
      role={!canAssignSelectedPlayer && canEdit ? 'button' : undefined}
      tabIndex={!canAssignSelectedPlayer && canEdit ? 0 : undefined}
      onClick={!canAssignSelectedPlayer && canEdit ? onSelect : undefined}
      onKeyDown={(event) => {
        if (!canAssignSelectedPlayer && canEdit && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{ minHeight: { xs: workspacePlayerTileCompactHeight, sm: workspacePlayerTileHeight }, p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center', justifyContent: 'center', borderStyle: 'dashed', borderWidth: isOver || invalidGenderTarget ? 2 : 1, borderColor: invalidGenderTarget ? 'error.main' : isOver ? 'primary.main' : 'divider', bgcolor: invalidGenderTarget ? 'rgba(211, 47, 47, 0.08)' : isOver ? 'action.selected' : 'background.default', cursor: canEdit ? 'pointer' : 'default' }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
      {canAssignSelectedPlayer && (
        <Button
          size="small"
          variant="contained"
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
        >
          Assign {selectedPoolPlayerName}
        </Button>
      )}
    </Paper>
  );
}

function WorkspacePoolDropZone({ children, canEdit }: { children: ReactNode; canEdit: boolean }) {
  const { isOver, setNodeRef } = useDroppable({ id: 'pool:remove', disabled: !canEdit });
  return (
    <Box ref={setNodeRef} sx={{ borderRadius: 2, outline: isOver ? '3px solid' : undefined, outlineColor: isOver ? 'primary.light' : undefined }}>
      {children}
    </Box>
  );
}

function strategyFromRow(row: StrategyProfileRow): RecommendationStrategyProfile {
  return {
    id: row.id,
    name: row.name,
    advancementType: row.advancement_type,
    version: row.version || 1,
    competitivenessWeight: row.competitiveness_weight,
    participationFairnessWeight: row.participation_fairness_weight,
    singlesOpportunityWeight: row.singles_opportunity_weight,
    scheduleSpacingWeight: row.schedule_spacing_weight,
    partnerCompatibilityWeight: row.partner_compatibility_weight,
    homeAwayBalanceWeight: row.home_away_balance_weight,
    singlesEdrWeight: row.singles_edr_weight,
    singlesUtrWeight: row.singles_utr_weight,
    doublesEdrWeight: row.doubles_edr_weight,
    doublesUtrWeight: row.doubles_utr_weight,
    changeSensitivity: row.change_sensitivity,
    tentativePlayerPolicy: row.tentative_player_policy,
    preferredPairBonus: row.preferred_pair_bonus ?? 20,
    preferredPairSeparationPenalty: row.preferred_pair_separation_penalty ?? -10,
    stronglyPreferredPairBonus: row.strongly_preferred_pair_bonus ?? 40,
    stronglyPreferredPairSeparationPenalty: row.strongly_preferred_pair_separation_penalty ?? -30,
    avoidPairingPenalty: row.avoid_pairing_penalty ?? -20,
    poorCompatibilityPenalty: row.poor_compatibility_penalty ?? -10,
    goodCompatibilityBonus: row.good_compatibility_bonus ?? 15,
    excellentCompatibilityBonus: row.excellent_compatibility_bonus ?? 30,
    multiTeamScheduling: normalizeMultiTeamSchedulingConfig(row.multi_team_scheduling_config),
    enforceTargetMatches: row.enforce_target_matches === true,
    targetMatchVariance: (row.target_match_variance === 0 || row.target_match_variance === 2 ? row.target_match_variance : 1),
    enforceMaxSit: row.enforce_max_sit === true,
    maxWeeksWithoutMatch: row.max_weeks_without_match ?? 2,
    usePartnerCompatibility: row.use_partner_compatibility !== false,
    usePartnerSchedulingPreferences: row.use_partner_scheduling_preferences !== false,
  };
}

export function LineupBuilder({
  matchId,
  lookups,
  onBackToMatch,
  onOpenAvailability,
  onOpenMatchDetail,
  onOpenTeamEvents,
  userPreferences,
  onUserPreferencesChange,
  timeFormat,
  readOnly = false,
  canViewPartnerPreferences = true,
  breadcrumbOrigin = 'team-events',
  isWorkflowFullScreen = false,
  onExitWorkflowFullScreen,
}: LineupBuilderProps) {
  const auditUserId = useAuditUserId();
  const { isOwner } = useAuth();
  const [match, setMatch] = useState<MatchEventRecord | null>(null);
  const [positions, setPositions] = useState<LeagueConfigurationPositionRecord[]>([]);
  const [lineStatuses, setLineStatuses] = useState<Record<string, LineStatus>>({});
  const [lineLogistics, setLineLogistics] = useState<Record<string, LineLogisticsOverrides>>({});
  const [lineLogisticsPositionId, setLineLogisticsPositionId] = useState<string | null>(null);
  const [lineLogisticsDraft, setLineLogisticsDraft] = useState<LineLogisticsOverrides>({});
  const [assignCourtsOpen, setAssignCourtsOpen] = useState(false);
  const [courtStartNumber, setCourtStartNumber] = useState('1');
  const [players, setPlayers] = useState<RosterPlayer[]>([]);
  const [entries, setEntries] = useState<LineupEntryRecord[]>([]);
  const [standbyPlayers, setStandbyPlayers] = useState<StandbyPlayerRecord[]>([]);
  const [partnerPreferences, setPartnerPreferences] = useState<PartnerPreferenceRow[]>([]);
  const [pairingRuleOverrides, setPairingRuleOverrides] = useState<PairingRuleOverrideRow[]>([]);
  const [participationRows, setParticipationRows] = useState<ParticipationReportRow[]>([]);
  const [remainingAvailableOpportunitiesByPlayer, setRemainingAvailableOpportunitiesByPlayer] = useState<Map<string, number>>(new Map());
  const [conflicts, setConflicts] = useState<ConflictRow[]>([]);
  const [sharedPlayerSchedule, setSharedPlayerSchedule] = useState<SharedPlayerScheduleAssignment[]>([]);
  const [crossTeamPolicyTeamIds, setCrossTeamPolicyTeamIds] = useState<string[]>([]);
  const [strategyProfile, setStrategyProfile] = useState<RecommendationStrategyProfile | null>(null);
  const [recommendationAnalysis, setRecommendationAnalysis] = useState<RecommendationAnalysis | null>(null);
  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);
  const [lineupHealthAnchor, setLineupHealthAnchor] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [lineupSaveState, setLineupSaveState] = useState<'saved' | 'saving' | 'error'>('saved');
  const [selectedPoolPlayerId, setSelectedPoolPlayerId] = useState<string | null>(null);
  const [activeDragPlayerId, setActiveDragPlayerId] = useState<string | null>(null);
  const [activePartnerContext, setActivePartnerContext] = useState<ActivePartnerContext | null>(null);
  const [planningSignalsAnchor, setPlanningSignalsAnchor] = useState<HTMLElement | null>(null);
  const [lineMenuAnchor, setLineMenuAnchor] = useState<HTMLElement | null>(null);
  const [lineMenuPositionId, setLineMenuPositionId] = useState<string | null>(null);
  const [publishMenuAnchor, setPublishMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSessionId, setCorrectionSessionId] = useState<string | null>(null);
  useEffect(() => { setCorrectionSessionId(null); }, [matchId]);
  const [lifecycleHistory, setLifecycleHistory] = useState<MatchLifecycleHistoryRow[]>([]);
  const [scoresheetGenerating, setScoresheetGenerating] = useState(false);
  const [standbyPlayerId, setStandbyPlayerId] = useState('');
  const [standbyNotes, setStandbyNotes] = useState('');
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [cancellationForm, setCancellationForm] = useState<CancellationForm>({
    entryId: '',
    cancellationType: 'cancelled_late',
    reason: 'Injury',
    replacementPlayerId: '',
    notes: '',
    cancellationCountsTowardAllocation: true,
    manualAllocationOverride: false,
    replacementAllocationOverride: false,
  });
  const [snack, setSnack] = useState<Snack | null>(null);
  const entriesRef = useRef<LineupEntryRecord[]>([]);
  const workspaceMovePendingRef = useRef(false);
  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'lineup-builder', contextKey: matchId ?? undefined, ready: !loading });
  const initialLineupLocalDraft = useMemo<LineupLocalDraft>(() => ({
    standbyPlayerId: '',
    standbyNotes: '',
    cancellationForm: {
      entryId: '',
      cancellationType: 'cancelled_late',
      reason: 'Injury',
      replacementPlayerId: '',
      notes: '',
      cancellationCountsTowardAllocation: true,
      manualAllocationOverride: false,
      replacementAllocationOverride: false,
    },
  }), []);
  const currentLineupLocalDraft = useMemo<LineupLocalDraft>(() => ({
    standbyPlayerId,
    standbyNotes,
    cancellationForm,
  }), [cancellationForm, standbyNotes, standbyPlayerId]);
  const lineupDraftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: 'lineup',
    entityId: matchId || 'unknown',
    value: currentLineupLocalDraft,
    initialValue: initialLineupLocalDraft,
    enabled: Boolean(matchId) && !readOnly,
  });

  const participationByPlayerId = useMemo(() => new Map(participationRows.map((row) => [row.playerId, row])), [participationRows]);
  const playersWithParticipationMetrics = useMemo(() => players.map((player) => {
    const participation = participationByPlayerId.get(player.id);
    if (!participation) return player;
    return {
      ...player,
      target_match_count: participation.adjustedTarget,
      completed_played: participation.totalPlayed,
      completed_singles: participation.singlesPlayed,
      completed_doubles: participation.doublesPlayed,
      future_singles: participation.futureSingles,
      future_doubles: participation.futureDoubles,
      unplayed_non_completed: participation.totalFuture,
      participation_status_delta: participation.statusDelta,
    };
  }), [participationByPlayerId, players]);
  const playersById = useMemo(() => new Map(playersWithParticipationMetrics.map((player) => [player.id, player])), [playersWithParticipationMetrics]);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);
  const loadedMatchWorkspaceId = useMemo(() => (match ? lookups.teams.find((team) => team.value === match.team_id)?.workspace_id || '' : ''), [lookups.teams, match]);
  const isMixedDoublesTeam = useMemo(() => {
    if (!match) return false;
    return lookups.teams.find((team) => team.value === match.team_id)?.team_gender_type === 'mixed';
  }, [lookups.teams, match]);
  const applicablePartnerPreferences = useMemo(() => {
    if (!isMixedDoublesTeam) return partnerPreferences;
    return partnerPreferences.filter((preference) => {
      const playerA = playersById.get(preference.player1_id);
      const playerB = playersById.get(preference.player2_id);
      return Boolean(playerA && playerB && playerA.sex !== playerB.sex);
    });
  }, [isMixedDoublesTeam, partnerPreferences, playersById]);
  const standbyPlayerIds = useMemo(() => new Set(standbyPlayers.map((standby) => standby.player_id)), [standbyPlayers]);
  const groupedPlayers = useMemo(() => groupedRosterPlayers(playersWithParticipationMetrics, userPreferences), [playersWithParticipationMetrics, userPreferences]);
  const playerRatingBasis = useMemo(
    () => (lookups.teams.find((team) => team.value === match?.team_id)?.player_rating_basis || 'edr') as PlayerRatingBasis,
    [lookups.teams, match?.team_id],
  );
  const groupedStandbyPlayers = useMemo(
    () => groupedRosterPlayers(playersWithParticipationMetrics.filter((player) => !standbyPlayerIds.has(player.id)), userPreferences),
    [playersWithParticipationMetrics, standbyPlayerIds, userPreferences],
  );
  const entryBySlot = useMemo(() => {
    const map = new Map<string, LineupEntryRecord>();
    entries.forEach((entry) => map.set(slotKey(entry.league_configuration_position_id, entry.slot_number), entry));
    return map;
  }, [entries]);
  const slots = useMemo<Slot[]>(() => {
    return positions.flatMap((position) =>
      Array.from({ length: position.required_player_count }, (_, index) => ({ position, slotNumber: index + 1 })),
    );
  }, [positions]);
  const lineStatusForPosition = (positionId: string): LineStatus => lineStatuses[positionId] || 'normal';
  const isDefaultedPosition = (positionId: string): boolean => defaultedLineStatuses.has(lineStatusForPosition(positionId));
  const selectedCancellationEntry = useMemo(() => entries.find((entry) => entry.id === cancellationForm.entryId), [cancellationForm.entryId, entries]);
  const selectedCancellationPlayer = selectedCancellationEntry ? playersById.get(selectedCancellationEntry.player_id) : undefined;
  const selectedCancellationPosition = selectedCancellationEntry ? positions.find((position) => position.id === selectedCancellationEntry.league_configuration_position_id) : undefined;

  const isMobileHealthOverlay = useMediaQuery('(max-width:899px)');
  const lineupHealthOpen = Boolean(lineupHealthAnchor);
  const closeLineupHealth = () => setLineupHealthAnchor(null);

  const partnerCompatibility = (playerAId: string, playerBId: string) => {
    if (!match || !playerAId || !playerBId || playerAId === playerBId) {
      return { compatibility: 'Unknown' as PartnerCompatibility, pairingRule: 'none' as PartnerPairingRule, notes: null as string | null, preference: null as PartnerPreferenceRow | null };
    }
    if (!loadedMatchWorkspaceId) return { compatibility: 'Unknown' as PartnerCompatibility, pairingRule: 'none' as PartnerPairingRule, notes: null as string | null, preference: null as PartnerPreferenceRow | null };
    const preference = partnerPreferenceForPair(applicablePartnerPreferences, loadedMatchWorkspaceId, playerAId, playerBId);
    return {
      compatibility: normalizeCompatibility(preference?.compatibility),
      pairingRule: normalizePairingRule(preference?.pairing_rule),
      notes: preference?.notes || null,
      preference,
    };
  };

  const resolvedPartnerContext = useMemo(() => {
    if (!activePartnerContext) return null;
    const entry = entries.find((item) => item.player_id === activePartnerContext.playerId && item.league_configuration_position_id === activePartnerContext.positionId);
    const position = positions.find((item) => item.id === activePartnerContext.positionId);
    const player = playersById.get(activePartnerContext.playerId);
    if (!entry || !position || !player || position.required_player_count < 2 || position.line_type === 'singles') return null;
    return { ...activePartnerContext, player, position };
  }, [activePartnerContext, entries, playersById, positions]);

  useEffect(() => {
    if (activePartnerContext && !resolvedPartnerContext) setActivePartnerContext(null);
  }, [activePartnerContext, resolvedPartnerContext]);

  const togglePartnerContext = (playerId: string, position: LeagueConfigurationPositionRecord) => {
    if (position.required_player_count < 2 || position.line_type === 'singles') return;
    setActivePartnerContext((current) => current?.playerId === playerId && current.positionId === position.id ? null : { playerId, positionId: position.id });
  };

  const slotPartnerIds = (positionId: string, slotNumber: number, currentEntryId?: string): string[] => {
    return entries
      .filter((entry) => entry.league_configuration_position_id === positionId && entry.slot_number !== slotNumber && entry.id !== currentEntryId)
      .map((entry) => entry.player_id);
  };

  const selectedLinePartnerDetails = (positionId: string) => {
    const lineEntries = entries
      .filter((entry) => entry.league_configuration_position_id === positionId)
      .sort((a, b) => a.slot_number - b.slot_number);
    if (lineEntries.length < 2) return null;
    return partnerCompatibility(lineEntries[0].player_id, lineEntries[1].player_id);
  };

  const lineupHealth = useMemo(() => {
    try {
      return buildLineupHealth({
        positions,
        entries,
        lineStatuses,
        playersById,
        opponentAverageEdr: match?.opponent_average_edr ?? undefined,
        workspaceId: loadedMatchWorkspaceId,
        partnerPreferences: applicablePartnerPreferences,
        participationRows,
        conflicts,
        playerLabel: (playerId) => playerName(playersById.get(playerId), userPreferences),
      });
    } catch (error) {
      console.error('Unable to calculate lineup health.', error);
      return fallbackLineupHealthSummary();
    }
  }, [applicablePartnerPreferences, conflicts, entries, lineStatuses, loadedMatchWorkspaceId, match?.opponent_average_edr, participationRows, playersById, positions, userPreferences]);

  const incompletePositions = useMemo(() => {
    return positions.filter((position) => !isDefaultedPosition(position.id) && entries.filter((entry) => entry.league_configuration_position_id === position.id).length < position.required_player_count);
  }, [entries, lineStatuses, positions]);

  const assignedLineupEdrPlayers = useMemo(() => {
    const lineupPlayers: Array<RosterPlayer & { lineType: string | null }> = [];
    entries
      .map((entry) => {
        const position = positions.find((item) => item.id === entry.league_configuration_position_id);
        const player = playersById.get(entry.player_id);
        if (!position || !player || isDefaultedPosition(position.id)) return null;
        return { ...player, lineType: position.line_type };
      })
      .forEach((player) => {
        if (player) lineupPlayers.push(player);
      });
    return lineupPlayers;
  }, [entries, lineStatuses, playersById, positions]);

  const lineupEdrSummary = useMemo(() => {
    const assignedWithEdr = assignedLineupEdrPlayers.filter((player) => hasEdr(player.estimated_dynamic_rating)).length;
    const overall = calculateLineupAverageEdr(assignedLineupEdrPlayers);
    const difference = calculateEdrDifference(overall, match?.opponent_average_edr);
    return {
      overall,
      opponent: match?.opponent_average_edr ?? null,
      difference,
      assignedWithEdr,
    };
  }, [assignedLineupEdrPlayers, match?.opponent_average_edr]);

  const duplicateAssignments = useMemo(() => {
    const grouped = entries.reduce<Record<string, LineupEntryRecord[]>>((acc, entry) => {
      acc[entry.player_id] ||= [];
      acc[entry.player_id].push(entry);
      return acc;
    }, {});
    return Object.entries(grouped)
      .filter(([, playerEntries]) => playerEntries.length > 1)
      .map(([playerId, playerEntries]) => ({ playerId, entries: playerEntries.sort((a, b) => a.position_code.localeCompare(b.position_code) || a.slot_number - b.slot_number) }));
  }, [entries]);

  const duplicatePlayerIds = useMemo(() => new Set(duplicateAssignments.map((assignment) => assignment.playerId)), [duplicateAssignments]);

  const genderWarnings = useMemo(() => {
    return positions.flatMap((position) => {
      if (isDefaultedPosition(position.id)) return [];
      if (position.required_male_count === 0 && position.required_female_count === 0) return [];
      const positionPlayers = entries
        .filter((entry) => entry.league_configuration_position_id === position.id)
        .map((entry) => playersById.get(entry.player_id))
        .filter(Boolean) as RosterPlayer[];
      const maleCount = positionPlayers.filter((player) => player.sex === 'male').length;
      const femaleCount = positionPlayers.filter((player) => player.sex === 'female').length;
      const warnings: string[] = [];
      if (maleCount !== position.required_male_count && positionPlayers.length === position.required_player_count) {
        warnings.push(`${position.position_code} requires ${position.required_male_count} male player${position.required_male_count === 1 ? '' : 's'}.`);
      }
      if (femaleCount !== position.required_female_count && positionPlayers.length === position.required_player_count) {
        warnings.push(`${position.position_code} requires ${position.required_female_count} female player${position.required_female_count === 1 ? '' : 's'}.`);
      }
      return warnings;
    });
  }, [entries, lineStatuses, playersById, positions]);

  const sameDayConflictForPlayer = (playerId: string): ConflictRow | undefined => conflicts.find((item) => item.player_id === playerId);

  const sameDayConflictLabel = (conflict: ConflictRow): string => {
    const assignment = `${lookupLabel(lookups, 'teams', conflict.team_id)} vs ${conflict.opponent_name || 'TBD'}${conflict.position_code ? `, ${conflict.position_code}` : ''}`;
    return `already scheduled today: ${assignment}`;
  };

  const workspaceTileSignals = useMemo(() => {
    const genericByPlayerId = new Map<string, WorkspaceTileSignal[]>();
    const poolByPlayerId = new Map<string, WorkspaceTileSignal[]>();
    const assignedIds = new Set(entries.map((entry) => entry.player_id));
    const matchDate = match?.match_date || '';
    const participationByPlayerId = new Map(participationRows.map((row) => [row.playerId, row]));
    const maxSitDays = strategyProfile?.enforceMaxSit && strategyProfile.maxWeeksWithoutMatch
      ? Number(strategyProfile.maxWeeksWithoutMatch) * 7
      : 0;

    playersWithParticipationMetrics.forEach((player) => {
      const isAssigned = assignedIds.has(player.id);
      const metrics = lineupTileMetricsForPlayer(player, isAssigned);
      const signals: WorkspaceTileSignal[] = [];
      const needed = metrics.targetDelta === null ? 0 : Math.max(0, -metrics.targetDelta);
      const availableOpportunities = remainingAvailableOpportunitiesByPlayer.get(player.id) || 0;
      if (needed > 0 && availableOpportunities > 0 && availableOpportunities <= needed) {
        signals.push({
          kind: 'target',
          title: 'Target Opportunity',
          detail: `${player.first_name} needs ${needed} more match${needed === 1 ? '' : 'es'} to reach the team target and is Available for only ${availableOpportunities} remaining match${availableOpportunities === 1 ? '' : 'es'}.`,
          priority: 100,
        });
      }
      const participation = participationByPlayerId.get(player.id);
      if (maxSitDays > 0 && participation?.lastPlayedDate && matchDate) {
        const elapsedDays = Math.max(0, Math.round((Date.parse(`${matchDate}T12:00:00Z`) - Date.parse(`${participation.lastPlayedDate}T12:00:00Z`)) / 86_400_000));
        if (elapsedDays >= Math.max(0, maxSitDays - 3)) {
          signals.push({
            kind: 'participation',
            title: 'Participation Priority',
            detail: `${player.first_name} has not played in ${elapsedDays} day${elapsedDays === 1 ? '' : 's'}; this team’s participation threshold is ${maxSitDays} days.`,
            priority: elapsedDays >= maxSitDays ? 90 : 70,
          });
        }
      }
      const sameDay = sameDayConflictForPlayer(player.id);
      const adjacentAssignment = sharedPlayerSchedule.find((assignment) => {
        if (!matchDate || assignment.playerId !== player.id || assignment.matchId === match?.id || assignment.matchStatus === 'Completed') return false;
        const daysApart = Math.abs((Date.parse(`${assignment.matchDate}T12:00:00Z`) - Date.parse(`${matchDate}T12:00:00Z`)) / 86_400_000);
        return daysApart <= 1;
      });
      if (sameDay || adjacentAssignment) {
        const detail = sameDay
          ? `${player.first_name} is already scheduled today: ${sameDayConflictLabel(sameDay)}.`
          : `${player.first_name} is already ${adjacentAssignment?.lineupStatus || 'scheduled'} for ${adjacentAssignment?.teamName || 'another team'} on ${adjacentAssignment?.matchDate}.`;
        signals.push({ kind: 'workload', title: sameDay ? 'Same-Day Workload' : 'Back-to-Back Match Days', detail, priority: sameDay ? -55 : -30 });
      }
      if (isAssigned && player.availability_status === 'maybe') {
        signals.push({ kind: 'maybe_assignment', title: 'Maybe Availability', detail: `${player.first_name} is assigned to this lineup but is currently marked Maybe. Confirm availability before the match.`, priority: -5 });
      }
      genericByPlayerId.set(player.id, signals);
      poolByPlayerId.set(player.id, [...signals]);
    });

    if (resolvedPartnerContext) {
      playersWithParticipationMetrics.forEach((player) => {
        if (assignedIds.has(player.id) || player.id === resolvedPartnerContext.playerId) return;
        const detail = partnerCompatibility(resolvedPartnerContext.playerId, player.id);
        let partnerSignal: WorkspaceTileSignal | null = null;
        if (detail.pairingRule === 'excluded' || detail.pairingRule === 'avoid_pairing') {
          partnerSignal = { kind: 'do_not_partner', title: 'Do Not Partner', detail: `${resolvedPartnerContext.player.first_name} and ${player.first_name} have a ${pairingRuleLabels[detail.pairingRule]} preference.${detail.notes ? ` ${detail.notes}` : ''}`, priority: -80 };
        } else if (['preferred', 'strongly_preferred', 'required_when_both_selected', 'schedule_only_as_pair'].includes(detail.pairingRule)) {
          partnerSignal = { kind: 'preferred_partner', title: 'Preferred Partner', detail: `${resolvedPartnerContext.player.first_name} has ${player.first_name} marked as ${pairingRuleLabels[detail.pairingRule]}.${detail.compatibility === 'Good' || detail.compatibility === 'Excellent' ? ` Their compatibility is also ${detail.compatibility}.` : ''}${detail.notes ? ` ${detail.notes}` : ''}`, priority: detail.pairingRule === 'strongly_preferred' ? 55 : 38 };
        } else if (detail.compatibility === 'Good' || detail.compatibility === 'Excellent') {
          partnerSignal = { kind: 'compatible_partner', title: 'Compatible Partner', detail: `${resolvedPartnerContext.player.first_name} and ${player.first_name} have ${detail.compatibility} compatibility.${detail.notes ? ` ${detail.notes}` : ''}`, priority: detail.compatibility === 'Excellent' ? 32 : 20 };
        }
        if (partnerSignal) poolByPlayerId.set(player.id, [...(poolByPlayerId.get(player.id) || []), partnerSignal]);
      });
    }
    return { genericByPlayerId, poolByPlayerId };
  }, [entries, match?.id, match?.match_date, participationRows, playersWithParticipationMetrics, remainingAvailableOpportunitiesByPlayer, resolvedPartnerContext, sharedPlayerSchedule, strategyProfile]);

  const lineupWorkspacePoolPlayers = useMemo(() => {
    const assignedIds = new Set(entries.map((entry) => entry.player_id));
    return sortWorkspacePoolPlayers(
      playersWithParticipationMetrics.filter((player) => !assignedIds.has(player.id)),
      (player) => (workspaceTileSignals.poolByPlayerId.get(player.id) || []).reduce((total, signal) => total + signal.priority, 0),
    );
  }, [entries, playersWithParticipationMetrics, workspaceTileSignals]);

  const lineupWorkspacePoolGroups = useMemo(
    () => groupWorkspacePoolPlayersByGender(lineupWorkspacePoolPlayers, userPreferences.groupPlayersBySex),
    [lineupWorkspacePoolPlayers, userPreferences.groupPlayersBySex],
  );

  const assignedWarnings = useMemo(() => {
    return entries.flatMap((entry) => {
      const player = playersById.get(entry.player_id);
      if (!player) return [];
      const messages: string[] = [];
      if (player.availability_status === 'maybe') messages.push(`${playerName(player, userPreferences)} is marked Maybe.`);
      if (player.availability_status === 'unknown') messages.push(`${playerName(player, userPreferences)} has Unknown availability.`);
      const conflict = conflicts.find((item) => item.player_id === player.id);
      if (conflict) messages.push(`${playerName(player, userPreferences)} is ${sameDayConflictLabel(conflict)}.`);
      const linePartnerIds = slotPartnerIds(entry.league_configuration_position_id, entry.slot_number, entry.id);
      linePartnerIds.forEach((partnerId) => {
        const detail = partnerCompatibility(entry.player_id, partnerId);
        if (detail.pairingRule === 'excluded') {
          messages.push(`${playerName(player, userPreferences)} and ${playerName(playersById.get(partnerId), userPreferences)} have a Do Not Pair scheduling preference.`);
        }
      });
      return messages;
    });
  }, [applicablePartnerPreferences, conflicts, entries, lookups, match, playersById, userPreferences]);

  const pairingRuleValidation = useMemo(() => {
    const hardErrors = new Set<string>();
    const warnings = new Set<string>();
    const overrideIds = new Set(pairingRuleOverrides.map((override) => override.partner_preference_id));
    const positionById = new Map(positions.map((position) => [position.id, position]));
    const assignedPositionByPlayer = new Map<string, LeagueConfigurationPositionRecord>();
    const assignedDoublesPlayers = new Set<string>();
    entries.forEach((entry) => {
      const position = positionById.get(entry.league_configuration_position_id);
      if (!position || position.line_type === 'singles') return;
      assignedDoublesPlayers.add(entry.player_id);
      assignedPositionByPlayer.set(entry.player_id, position);
    });

    const name = (playerId: string) => playerName(playersById.get(playerId), userPreferences) || lookupLabel(lookups, 'players', playerId);

    applicablePartnerPreferences.forEach((preference) => {
      if (preference.is_active === false) return;
      const rule = normalizePairingRule(preference.pairing_rule);
      if (rule === 'none') return;
      const firstPosition = assignedPositionByPlayer.get(preference.player1_id);
      const secondPosition = assignedPositionByPlayer.get(preference.player2_id);
      const bothAssignedToDoubles = Boolean(firstPosition && secondPosition);
      const pairLabel = `${name(preference.player1_id)} and ${name(preference.player2_id)}`;
      if (rule === 'excluded' && bothAssignedToDoubles && firstPosition?.id === secondPosition?.id) {
        const message = `${pairLabel} have a Do Not Pair scheduling preference and cannot share the same doubles line.`;
        if (overrideIds.has(preference.id)) warnings.add(`${message} Override recorded for this match.`);
        else hardErrors.add(message);
      } else if (rule === 'required_when_both_selected' && bothAssignedToDoubles && firstPosition?.id !== secondPosition?.id) {
        const message = `${pairLabel} must be paired when both play in this lineup.`;
        if (overrideIds.has(preference.id)) warnings.add(`${message} Override recorded for this match.`);
        else hardErrors.add(message);
      } else if (rule === 'schedule_only_as_pair') {
        const firstAssigned = assignedDoublesPlayers.has(preference.player1_id);
        const secondAssigned = assignedDoublesPlayers.has(preference.player2_id);
        if (firstAssigned !== secondAssigned || (bothAssignedToDoubles && firstPosition?.id !== secondPosition?.id)) {
          const message = `${pairLabel} are Schedule Only as a Pair and must be assigned together on the same doubles line.`;
          if (overrideIds.has(preference.id)) warnings.add(`${message} Override recorded for this match.`);
          else hardErrors.add(message);
        }
      } else if (rule === 'avoid_pairing' && bothAssignedToDoubles && firstPosition?.id === secondPosition?.id) {
        warnings.add(`${pairLabel} have an Avoid Pairing scheduling preference.`);
      } else if (pairingRuleIsSoft(rule) && bothAssignedToDoubles && firstPosition?.id !== secondPosition?.id) {
        warnings.add(`${pairLabel} ${rule === 'strongly_preferred' ? 'strongly prefer' : 'prefer'} to play together.`);
      }
    });

    return { hardErrors: Array.from(hardErrors), warnings: Array.from(warnings) };
  }, [applicablePartnerPreferences, entries, lookups, pairingRuleOverrides, playersById, positions, userPreferences]);

  const hardPairingViolationPreferences = useMemo(() => {
    const overrideIds = new Set(pairingRuleOverrides.map((override) => override.partner_preference_id));
    const positionById = new Map(positions.map((position) => [position.id, position]));
    const assignedPositionByPlayer = new Map<string, LeagueConfigurationPositionRecord>();
    const assignedDoublesPlayers = new Set<string>();
    entries.forEach((entry) => {
      const position = positionById.get(entry.league_configuration_position_id);
      if (!position || position.line_type === 'singles') return;
      assignedDoublesPlayers.add(entry.player_id);
      assignedPositionByPlayer.set(entry.player_id, position);
    });
    return applicablePartnerPreferences.filter((preference) => {
      if (preference.is_active === false || overrideIds.has(preference.id)) return false;
      const rule = normalizePairingRule(preference.pairing_rule);
      const firstPosition = assignedPositionByPlayer.get(preference.player1_id);
      const secondPosition = assignedPositionByPlayer.get(preference.player2_id);
      const bothAssignedToDoubles = Boolean(firstPosition && secondPosition);
      if (rule === 'excluded') return bothAssignedToDoubles && firstPosition?.id === secondPosition?.id;
      if (rule === 'required_when_both_selected') return bothAssignedToDoubles && firstPosition?.id !== secondPosition?.id;
      if (rule === 'schedule_only_as_pair') {
        const firstAssigned = assignedDoublesPlayers.has(preference.player1_id);
        const secondAssigned = assignedDoublesPlayers.has(preference.player2_id);
        return firstAssigned !== secondAssigned || (bothAssignedToDoubles && firstPosition?.id !== secondPosition?.id);
      }
      return false;
    });
  }, [applicablePartnerPreferences, entries, pairingRuleOverrides, positions]);

  const assignedLabelForPlayer = (playerId: string, currentEntryId?: string): string | null => {
    const otherEntry = entries.find((entry) => entry.player_id === playerId && entry.id !== currentEntryId);
    if (!otherEntry) return null;
    return `Already assigned to ${otherEntry.position_code} Slot ${otherEntry.slot_number}`;
  };

  const duplicateWarnings = useMemo(() => {
    return duplicateAssignments.map((assignment) => {
      const player = playersById.get(assignment.playerId);
      const locations = assignment.entries.map((entry) => `${entry.position_code} Slot ${entry.slot_number}`).join(', ');
      return `${playerName(player, userPreferences)} is assigned more than once: ${locations}.`;
    });
  }, [duplicateAssignments, playersById, userPreferences]);

  const standbyLineupOverlapWarnings = useMemo(() => {
    return standbyPlayers
      .map((standby) => {
        const assignedLabel = assignedLabelForPlayer(standby.player_id);
        if (!assignedLabel) return null;
        return `${playerName(playersById.get(standby.player_id), userPreferences)} is listed as standby and is also ${assignedLabel.toLowerCase()}.`;
      })
      .filter(Boolean) as string[];
  }, [playersById, standbyPlayers, userPreferences]);

  const canEdit = Boolean(!readOnly && match && (match.match_status !== 'Completed' || (isOwner && correctionSessionId)));
  const canRecordCancellation = Boolean(canEdit && match?.lineup_status === 'Published' && match.match_status !== 'Completed');

  const beginCorrection = async () => {
    if (!match || !isOwner || correctionReason.trim().length < 10) return;
    const { data, error } = await supabase.rpc('begin_lineup_correction', {
      p_match_event_id: match.id,
      p_reason: correctionReason.trim(),
    });
    if (error || !data) {
      setSnack({ severity: 'error', message: error?.message || 'Unable to open audited correction.' });
      return;
    }
    setCorrectionSessionId(String(data));
    setCorrectionDialogOpen(false);
    setCorrectionReason('');
    setSnack({ severity: 'info', message: 'Audited correction open for 10 minutes. Finish when your changes are saved.' });
  };

  const endCorrection = async () => {
    if (!correctionSessionId) return;
    const { error } = await supabase.rpc('end_lineup_correction', { p_session_id: correctionSessionId });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setCorrectionSessionId(null);
    setSnack({ severity: 'success', message: 'Audited correction closed.' });
  };

  const loadLineup = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    const matchResult = await supabase.from('match_events').select('*').eq('id', matchId).is('deleted_at', null).single();
    if (matchResult.error || !matchResult.data) {
      setLoading(false);
      setSnack({ severity: 'error', message: matchResult.error?.message || 'Match not found.' });
      return;
    }

    const loadedMatch = matchResult.data as unknown as MatchEventRecord;
    const positionsResult = await supabase
      .from('league_configuration_positions')
      .select('*')
      .eq('league_configuration_id', loadedMatch.league_configuration_id)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('display_order', { ascending: true });

    const rosterResult = await supabase
      .from('team_rosters')
      .select('player_id, player_tier, roster_status, roster_role, target_match_count, team_priority')
      .eq('team_id', loadedMatch.team_id)
      .eq('is_active', true)
      .in('roster_status', ['active', 'substitute'])
      .is('deleted_at', null);

    if (positionsResult.error || rosterResult.error) {
      setLoading(false);
      setSnack({ severity: 'error', message: positionsResult.error?.message || rosterResult.error?.message || 'Unable to load lineup setup.' });
      return;
    }

    const rosterRows = (rosterResult.data || []) as unknown as RosterRow[];
    const playerIds = rosterRows.map((row) => row.player_id);

    const loadedMatchWorkspaceId = lookups.teams.find((team) => team.value === loadedMatch.team_id)?.workspace_id || '';
    const workspaceTeamIds = loadedMatchWorkspaceId
      ? lookups.teams.filter((team) => team.workspace_id === loadedMatchWorkspaceId).map((team) => team.value)
      : [loadedMatch.team_id];
    let applicablePolicyTeamIds: string[] = [];
    let applicablePolicyConfig: unknown = null;
    const policyMembershipResult = await supabase
      .from('cross_team_scheduling_policy_teams')
      .select('policy_id')
      .eq('team_id', loadedMatch.team_id)
      .is('deleted_at', null)
      .maybeSingle();
    if (!policyMembershipResult.error && policyMembershipResult.data?.policy_id) {
      const [policyTeamsResult, policyResult] = await Promise.all([
        supabase
          .from('cross_team_scheduling_policy_teams')
          .select('team_id')
          .eq('policy_id', policyMembershipResult.data.policy_id)
          .is('deleted_at', null),
        supabase
          .from('cross_team_scheduling_policies')
          .select('scheduling_config')
          .eq('id', policyMembershipResult.data.policy_id)
          .is('deleted_at', null)
          .maybeSingle(),
      ]);
      if (!policyTeamsResult.error) {
        applicablePolicyTeamIds = ((policyTeamsResult.data || []) as unknown as Array<{ team_id: string }>).map((row) => row.team_id);
      }
      if (!policyResult.error) {
        applicablePolicyConfig = (policyResult.data as { scheduling_config?: unknown } | null)?.scheduling_config || null;
      }
    }
    const [playersResult, availabilityResult, entriesResult, standbyResult, conflictResult, completedResult, futureResult, lineStatusesResult, partnerResult, pairingOverrideResult, participationResult, teamStrategyResult, lifecycleHistoryResult, sharedScheduleResult, sharedRosterResult] = await Promise.all([
      playerIds.length > 0
        ? supabase
            .from('players')
            .select('id, first_name, last_name, email, sex, ntrp_rating, estimated_dynamic_rating, utr_singles_rating, utr_doubles_rating, singles_preference, back_to_back_match_day_tolerance, same_day_match_tolerance, minimum_recovery_hours')
            .in('id', playerIds)
            .eq('is_active', true)
            .is('deleted_at', null)
            .order('last_name', { ascending: true })
        : Promise.resolve({ data: [], error: null }),
      playerIds.length > 0
        ? supabase.from('availability_records').select('player_id, availability_status').eq('match_event_id', loadedMatch.id).in('player_id', playerIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      supabase.from('lineup_entries').select('*').eq('match_event_id', loadedMatch.id).is('deleted_at', null).order('position_code').order('slot_number'),
      supabase.from('standby_players').select('*').eq('match_event_id', loadedMatch.id).is('deleted_at', null).order('created_at'),
      playerIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('player_id, match_event_id, league_configuration_positions(position_code), match_events!inner(id, match_date, timezone, opponent_name, team_id, match_status, lineup_status, deleted_at)')
            .in('player_id', playerIds)
            .neq('match_event_id', loadedMatch.id)
            .is('deleted_at', null)
            .eq('match_events.match_date', loadedMatch.match_date)
            .neq('match_events.match_status', 'Cancelled')
            .in('match_events.lineup_status', ['Draft', 'Published'])
            .is('match_events.deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      playerIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('player_id, match_event_id, league_configuration_positions(line_type), match_events!inner(id, team_id, match_date, timezone, match_time, match_status, deleted_at)')
            .in('player_id', playerIds)
            .is('deleted_at', null)
            .eq('match_events.team_id', loadedMatch.team_id)
            .eq('match_events.match_status', 'Completed')
            .is('match_events.deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      playerIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('player_id, match_event_id, league_configuration_positions(line_type), match_events!inner(id, team_id, match_date, timezone, match_time, match_status, lineup_status, deleted_at)')
            .in('player_id', playerIds)
            .is('deleted_at', null)
            .eq('match_events.team_id', loadedMatch.team_id)
            .neq('match_events.match_status', 'Cancelled')
            .neq('match_events.match_status', 'Completed')
            .in('match_events.lineup_status', ['Draft', 'Published'])
            .is('match_events.deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      supabase.from('lineup_position_statuses').select('*').eq('match_event_id', loadedMatch.id),
      loadedMatchWorkspaceId && canViewPartnerPreferences
        ? supabase
            .from('partner_preferences')
            .select('id, workspace_id, team_id, player1_id, player2_id, compatibility, pairing_rule, is_active, notes')
            .eq('workspace_id', loadedMatchWorkspaceId)
            .eq('is_active', true)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from('lineup_pairing_rule_overrides')
        .select('id, match_event_id, partner_preference_id, pairing_rule, override_reason, created_by, created_at')
        .eq('match_event_id', loadedMatch.id)
        .is('deleted_at', null),
      loadParticipationReportRows({
        teamId: loadedMatch.team_id,
        includeInactivePlayers: false,
        includeSubstitutePlayers: true,
        userPreferences,
        playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
      })
        .then((result) => ({ data: result.rows, error: null }))
        .catch((error: unknown) => ({ data: [] as ParticipationReportRow[], error: error instanceof Error ? error : new Error('Unable to load participation summaries.') })),
      supabase
        .from('teams')
        .select('advancement_type, recommendation_strategy_profile_id, enforce_target_matches, target_match_variance, enforce_max_sit, max_weeks_without_match, use_partner_compatibility, use_partner_scheduling_preferences')
        .eq('id', loadedMatch.team_id)
        .single(),
      supabase
        .from('match_lifecycle_history')
        .select('id, match_event_id, previous_lifecycle_state, new_lifecycle_state, changed_at, changed_by, change_reason')
        .eq('match_event_id', loadedMatch.id)
        .order('changed_at', { ascending: false }),
      playerIds.length > 0 && workspaceTeamIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('player_id, match_event_id, position_code, league_configuration_positions(line_type), match_events!inner(id, team_id, match_date, timezone, match_time, warmup_time, opponent_name, match_site_type, match_status, lineup_status, facility_id, deleted_at)')
            .in('player_id', playerIds)
            .in('match_events.team_id', workspaceTeamIds)
            .is('deleted_at', null)
            .neq('match_events.match_status', 'Cancelled')
            .is('match_events.deleted_at', null)
            .limit(500)
        : Promise.resolve({ data: [], error: null }),
      playerIds.length > 0 && workspaceTeamIds.length > 0
        ? supabase
            .from('team_rosters')
            .select('team_id, player_id, team_priority')
            .in('team_id', workspaceTeamIds)
            .in('player_id', playerIds)
            .is('deleted_at', null)
            .eq('is_active', true)
        : Promise.resolve({ data: [], error: null }),
    ]);

    setLoading(false);

    if (playersResult.error || availabilityResult.error || entriesResult.error || standbyResult.error || conflictResult.error || completedResult.error || futureResult.error || lineStatusesResult.error || pairingOverrideResult.error || participationResult.error || sharedScheduleResult.error || sharedRosterResult.error) {
      setSnack({
        severity: 'error',
        message:
          playersResult.error?.message ||
          availabilityResult.error?.message ||
          entriesResult.error?.message ||
          standbyResult.error?.message ||
          conflictResult.error?.message ||
          completedResult.error?.message ||
          futureResult.error?.message ||
          lineStatusesResult.error?.message ||
          pairingOverrideResult.error?.message ||
          participationResult.error?.message ||
          sharedScheduleResult.error?.message ||
          sharedRosterResult.error?.message ||
          'Unable to load lineup.',
      });
      return;
    }
    if (teamStrategyResult.error && !/advancement_type|recommendation_strategy_profile_id/i.test(teamStrategyResult.error.message)) {
      setSnack({ severity: 'error', message: teamStrategyResult.error.message });
      return;
    }

    // Target Opportunity is intentionally based on actual remaining Available
    // match opportunities, not merely a negative target delta. This keeps the
    // pool signal aligned with the recommendation engine's participation logic.
    const remainingAvailableOpportunities = new Map<string, number>();
    if (playerIds.length > 0) {
      const remainingMatchesResult = await supabase
        .from('match_events')
        .select('id')
        .eq('team_id', loadedMatch.team_id)
        .gte('match_date', loadedMatch.match_date)
        .neq('match_status', 'Cancelled')
        .neq('match_status', 'Completed')
        .is('deleted_at', null);
      const remainingMatchIds = ((remainingMatchesResult.data || []) as Array<{ id: string }>).map((row) => row.id);
      if (!remainingMatchesResult.error && remainingMatchIds.length > 0) {
        const remainingAvailabilityResult = await supabase
          .from('availability_records')
          .select('player_id, availability_status')
          .in('match_event_id', remainingMatchIds)
          .in('player_id', playerIds)
          .eq('availability_status', 'available')
          .is('deleted_at', null);
        if (!remainingAvailabilityResult.error) {
          ((remainingAvailabilityResult.data || []) as Array<{ player_id: string }>).forEach((row) => {
            remainingAvailableOpportunities.set(row.player_id, (remainingAvailableOpportunities.get(row.player_id) || 0) + 1);
          });
        }
      }
    }

    const availabilityByPlayer = new Map(
      ((availabilityResult.data || []) as unknown as { player_id: string; availability_status: AvailabilityStatus }[]).map((row) => [row.player_id, row.availability_status]),
    );
    const rosterByPlayer = new Map(rosterRows.map((row) => [row.player_id, row]));
    const workloadByPlayer = new Map<string, WorkloadCounts>();
    const ensureWorkload = (playerId: string): WorkloadCounts => {
      const existing = workloadByPlayer.get(playerId);
      if (existing) return existing;
      const created = { completed_played: 0, completed_singles: 0, completed_doubles: 0, future_singles: 0, future_doubles: 0, unplayed_non_completed: 0 };
      workloadByPlayer.set(playerId, created);
      return created;
    };
    const completedAssignmentKeys = new Set<string>();
    ((completedResult.data || []) as unknown as Array<{ player_id: string; match_event_id: string; league_configuration_positions?: { line_type?: string | null } | Array<{ line_type?: string | null }> }>)
      .forEach((row) => {
        const assignmentKey = `${row.player_id}:${row.match_event_id}`;
        if (completedAssignmentKeys.has(assignmentKey)) return;
        completedAssignmentKeys.add(assignmentKey);
        const lineTypeRecord = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
        const workload = ensureWorkload(row.player_id);
        workload.completed_played += 1;
        if (lineTypeRecord?.line_type === 'singles') {
          workload.completed_singles += 1;
        } else {
          workload.completed_doubles += 1;
        }
      });
    const unplayedAssignmentKeys = new Set<string>();
    ((futureResult.data || []) as unknown as Array<{ player_id: string; match_event_id: string; league_configuration_positions?: { line_type?: string | null } | Array<{ line_type?: string | null }>; match_events?: { id: string; match_date: string; match_time?: string | null } | Array<{ id: string; match_date: string; match_time?: string | null }> }>)
      .forEach((row) => {
        const futureMatch = Array.isArray(row.match_events) ? row.match_events[0] : row.match_events;
        const currentDateTime = `${loadedMatch.match_date}T${loadedMatch.match_time || '00:00:00'}`;
        const futureDateTime = futureMatch ? `${futureMatch.match_date}T${futureMatch.match_time || '00:00:00'}` : '';
        // Unplayed means any saved non-completed assignment in this team and
        // season. The current match is represented separately by the live
        // workspace state so it is never double-counted.
        if (!futureMatch || futureMatch.id === loadedMatch.id) return;
        const assignmentKey = `${row.player_id}:${row.match_event_id}`;
        const workload = ensureWorkload(row.player_id);
        if (!unplayedAssignmentKeys.has(assignmentKey)) {
          unplayedAssignmentKeys.add(assignmentKey);
          workload.unplayed_non_completed += 1;
        }
        // The recommendation engine's existing future singles/doubles values
        // remain intentionally chronological relative to the match being
        // edited. They are distinct from the tile's Unplayed metric.
        if (futureDateTime <= currentDateTime) return;
        const lineTypeRecord = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
        if (lineTypeRecord?.line_type === 'singles') {
          workload.future_singles += 1;
        } else {
          workload.future_doubles += 1;
        }
      });
    const loadedPlayers = ((playersResult.data || []) as unknown as PlayerRow[])
      .map<RosterPlayer>((player) => ({
        ...player,
        utr_singles_rating: player.utr_singles_rating ?? null,
        utr_doubles_rating: player.utr_doubles_rating ?? null,
        singles_preference: player.singles_preference || 'if_possible',
        player_tier: rosterByPlayer.get(player.id)?.player_tier || null,
        roster_status: rosterByPlayer.get(player.id)?.roster_status || 'active',
        roster_role: normalizeRosterRole(rosterByPlayer.get(player.id)?.roster_role),
        target_match_count: rosterByPlayer.get(player.id)?.target_match_count ?? null,
        availability_status: availabilityByPlayer.get(player.id) || 'unknown',
        completed_played: workloadByPlayer.get(player.id)?.completed_played || 0,
        completed_singles: workloadByPlayer.get(player.id)?.completed_singles || 0,
        completed_doubles: workloadByPlayer.get(player.id)?.completed_doubles || 0,
        future_singles: workloadByPlayer.get(player.id)?.future_singles || 0,
        future_doubles: workloadByPlayer.get(player.id)?.future_doubles || 0,
        unplayed_non_completed: workloadByPlayer.get(player.id)?.unplayed_non_completed || 0,
        back_to_back_match_day_tolerance: player.back_to_back_match_day_tolerance || 'if_needed',
        same_day_match_tolerance: player.same_day_match_tolerance || 'if_needed',
        minimum_recovery_hours: player.minimum_recovery_hours ?? 16,
        team_priority: rosterByPlayer.get(player.id)?.team_priority || 'normal',
      }));

    const loadedConflicts = ((conflictResult.data || []) as unknown as Array<{
      player_id: string;
      match_event_id: string;
      league_configuration_positions?: { position_code?: string | null } | Array<{ position_code?: string | null }>;
      match_events?: { opponent_name: string | null; team_id: string } | Array<{ opponent_name: string | null; team_id: string }>;
    }>).map((row) => {
      const joined = Array.isArray(row.match_events) ? row.match_events[0] : row.match_events;
      const position = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
      return {
        player_id: row.player_id,
        match_event_id: row.match_event_id,
        opponent_name: joined?.opponent_name || null,
        team_id: joined?.team_id || '',
        position_code: position?.position_code || null,
      };
    });
    const teamNameById = new Map(lookups.teams.map((team) => [team.value, team.label]));
    const sharedTeamPriorityByCell = new Map(
      ((sharedRosterResult.data || []) as Array<{ team_id: string; player_id: string; team_priority: 'primary' | 'normal' | 'secondary' | null }>)
        .map((row) => [`${row.team_id}:${row.player_id}`, row.team_priority || 'normal']),
    );
    const loadedSharedSchedule = ((sharedScheduleResult.data || []) as unknown as Array<{
      player_id: string;
      match_event_id: string;
      position_code?: string | null;
      league_configuration_positions?: { line_type?: string | null } | Array<{ line_type?: string | null }>;
      match_events?: {
        id: string;
        team_id: string;
        match_date: string;
        match_time: string | null;
        warmup_time: string | null;
        opponent_name: string | null;
        match_site_type: MatchSiteType;
        match_status: string;
        lineup_status: LineupStatus;
        facility_id?: string | null;
      } | Array<{
        id: string;
        team_id: string;
        match_date: string;
        match_time: string | null;
        warmup_time: string | null;
        opponent_name: string | null;
        match_site_type: MatchSiteType;
        match_status: string;
        lineup_status: LineupStatus;
        facility_id?: string | null;
      }>;
    }>).map<SharedPlayerScheduleAssignment | null>((row) => {
      const joined = Array.isArray(row.match_events) ? row.match_events[0] : row.match_events;
      if (!joined) return null;
      const position = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
      const arrivalDateTime = joined.warmup_time || joined.match_time ? `${joined.match_date}T${joined.warmup_time || joined.match_time}` : null;
      return {
        playerId: row.player_id,
        matchId: joined.id || row.match_event_id,
        teamId: joined.team_id,
        teamName: teamNameById.get(joined.team_id) || null,
        opponentName: joined.opponent_name || null,
        matchDate: joined.match_date,
        matchTime: joined.match_time,
        arrivalDateTime,
        matchSiteType: joined.match_site_type,
        facilityId: joined.facility_id || null,
        positionCode: row.position_code || null,
        assignmentType: position?.line_type === 'singles' ? 'singles' : position?.line_type ? 'doubles' : 'unknown',
        lineupStatus: joined.lineup_status,
        matchStatus: joined.match_status,
        certainty: joined.lineup_status === 'Draft' ? 'provisional' : 'committed',
        source: joined.match_status === 'Completed' ? 'historical' : 'lineup',
        teamPriority: sharedTeamPriorityByCell.get(`${joined.team_id}:${row.player_id}`) || 'normal',
      };
    }).filter(Boolean) as SharedPlayerScheduleAssignment[];

    setMatch(loadedMatch);
    setPositions((positionsResult.data || []) as unknown as LeagueConfigurationPositionRecord[]);
    setLineStatuses(
      ((lineStatusesResult.data || []) as unknown as LineStatusRecord[]).reduce<Record<string, LineStatus>>((acc, row) => {
        acc[row.league_configuration_position_id] = row.line_status || 'normal';
        return acc;
      }, {}),
    );
    setLineLogistics(
      ((lineStatusesResult.data || []) as unknown as LineStatusRecord[]).reduce<Record<string, LineLogisticsOverrides>>((acc, row) => {
        acc[row.league_configuration_position_id] = row;
        return acc;
      }, {}),
    );
    setPlayers(loadedPlayers);
    setEntries((entriesResult.data || []) as unknown as LineupEntryRecord[]);
    setStandbyPlayers((standbyResult.data || []) as unknown as StandbyPlayerRecord[]);
    setPartnerPreferences(partnerResult.error ? [] : ((partnerResult.data || []) as unknown as PartnerPreferenceRow[]));
    setPairingRuleOverrides(pairingOverrideResult.error ? [] : ((pairingOverrideResult.data || []) as unknown as PairingRuleOverrideRow[]));
    setParticipationRows((participationResult.data || []) as ParticipationReportRow[]);
    setRemainingAvailableOpportunitiesByPlayer(remainingAvailableOpportunities);
    setConflicts(loadedConflicts);
    setSharedPlayerSchedule(loadedSharedSchedule);
    setCrossTeamPolicyTeamIds(applicablePolicyTeamIds);
    setLifecycleHistory(lifecycleHistoryResult.error ? [] : ((lifecycleHistoryResult.data || []) as unknown as MatchLifecycleHistoryRow[]));
    const teamStrategy = teamStrategyResult.error ? null : (teamStrategyResult.data as TeamStrategyRow | null);
    const advancementType = teamStrategy?.advancement_type || 'non_advancing';
    if (teamStrategy?.recommendation_strategy_profile_id) {
      const profileResult = await supabase
        .from('recommendation_strategy_profiles')
        .select('*')
        .eq('id', teamStrategy.recommendation_strategy_profile_id)
        .is('deleted_at', null)
        .maybeSingle();
      if (!profileResult.error && profileResult.data) {
        const strategy = strategyFromRow(profileResult.data as unknown as StrategyProfileRow);
        const configured = applyTeamRecommendationSettings(strategy, teamStrategy);
        setStrategyProfile(applicablePolicyConfig ? { ...configured, multiTeamScheduling: normalizeMultiTeamSchedulingConfig(applicablePolicyConfig) } : configured);
      } else {
        const strategy = defaultStrategyForAdvancementType(advancementType);
        const configured = applyTeamRecommendationSettings(strategy, teamStrategy);
        setStrategyProfile(applicablePolicyConfig ? { ...configured, multiTeamScheduling: normalizeMultiTeamSchedulingConfig(applicablePolicyConfig) } : configured);
      }
    } else {
      const strategy = defaultStrategyForAdvancementType(advancementType);
      const configured = applyTeamRecommendationSettings(strategy, teamStrategy);
      setStrategyProfile(applicablePolicyConfig ? { ...configured, multiTeamScheduling: normalizeMultiTeamSchedulingConfig(applicablePolicyConfig) } : configured);
    }
  }, [canViewPartnerPreferences, lookups, matchId, userPreferences]);

  useEffect(() => {
    void loadLineup();
  }, [loadLineup]);

  const refreshMatchOnly = async () => {
    if (!matchId) return;
    const { data, error } = await supabase.from('match_events').select('*').eq('id', matchId).is('deleted_at', null).single();
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setMatch(data as unknown as MatchEventRecord);
  };

  const refreshWorkspaceEntries = async () => {
    if (!matchId) return;
    const { data, error } = await supabase.from('lineup_entries')
      .select('*')
      .eq('match_event_id', matchId)
      .is('deleted_at', null)
      .order('position_code')
      .order('slot_number');
    if (error) throw new Error(error.message);
    const canonical = (data || []) as unknown as LineupEntryRecord[];
    entriesRef.current = canonical;
    setEntries(canonical);
    return canonical;
  };

  const refreshWorkspaceParticipation = async () => {
    if (!match) return;
    const result = await loadParticipationReportRows({
      teamId: match.team_id,
      includeInactivePlayers: false,
      includeSubstitutePlayers: true,
      userPreferences,
      playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
    });
    setParticipationRows(result.rows);
  };

  const markDraftIfNeeded = async () => {
    if (!match || match.lineup_status !== 'Not Started') return;
    const { data, error } = await supabase.from('match_events').update({ lineup_status: 'Draft' }).eq('id', match.id).select('*').single();
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setMatch(data as unknown as MatchEventRecord);
  };

  const openRecommendationDialog = () => {
    if (!match || !strategyProfile) {
      setSnack({ severity: 'error', message: 'Recommendation strategy is not ready yet.' });
      return;
    }
    const playerRatingBasis = (lookups.teams.find((team) => team.value === match.team_id)?.player_rating_basis || 'edr') as PlayerRatingBasis;
    const analysis = generateLineupRecommendationAnalysis({
      match: {
        id: match.id,
        teamId: match.team_id,
        seasonId: match.team_id,
        matchDate: match.match_date,
        matchTime: match.match_time,
        arrivalDateTime: match.warmup_time || match.match_time ? `${match.match_date}T${match.warmup_time || match.match_time}` : null,
        matchStatus: match.match_status,
        lineupStatus: match.lineup_status,
        matchSiteType: match.match_site_type,
        opponentName: match.opponent_name,
        opponentAverageEdr: match.opponent_average_edr,
        opponentRatingBasis: match.opponent_rating_basis || 'avg_edr',
        opponentSubjectiveDifficulty: match.opponent_subjective_difficulty ?? null,
      },
      positions: positions
        .filter((position) => !isDefaultedPosition(position.id))
        .map((position) => ({
          id: position.id,
          positionCode: position.position_code,
          displayOrder: position.display_order,
          lineType: position.line_type,
          requiredPlayerCount: position.required_player_count,
          requiredMaleCount: position.required_male_count,
          requiredFemaleCount: position.required_female_count,
        })),
      players: players.map((player) => {
        const participation = participationRows.find((row) => row.playerId === player.id);
        return {
          id: player.id,
          firstName: player.first_name,
          lastName: player.last_name,
          sex: player.sex,
          availabilityStatus: player.availability_status,
          rosterStatus: player.roster_status,
          rosterRole: player.roster_role,
          estimatedDynamicRating: player.estimated_dynamic_rating,
          utrSinglesRating: player.utr_singles_rating,
          utrDoublesRating: player.utr_doubles_rating,
          playerTier: player.player_tier,
          targetMatches: participation?.adjustedTarget ?? player.target_match_count ?? 0,
          completedPlayed: participation?.totalPlayed ?? player.completed_played,
          futureSingles: participation?.futureSingles ?? player.future_singles,
          futureDoubles: participation?.futureDoubles ?? player.future_doubles,
          singlesPlayed: participation?.singlesPlayed ?? player.completed_singles,
          homeMatches: participation?.homeMatches,
          awayMatches: participation?.awayMatches,
          neutralMatches: participation?.neutralMatches,
          totalAssignmentsForBalance: participation?.totalCompletedAndFutureScheduledMatches,
          lastPlayedDate: participation?.lastPlayedDate ?? null,
          lastPlayedWeeksAgo: participation?.lastPlayedWeeksAgo ?? null,
          backToBackTolerance: player.back_to_back_match_day_tolerance,
          sameDayTolerance: player.same_day_match_tolerance,
          minimumRecoveryHours: player.minimum_recovery_hours,
          teamPriority: player.team_priority,
        };
      }),
      entries: entries.map((entry) => ({
        id: entry.id,
        matchEventId: entry.match_event_id,
        leagueConfigurationPositionId: entry.league_configuration_position_id,
        positionCode: entry.position_code,
        slotNumber: entry.slot_number,
        playerId: entry.player_id,
      })),
      partnerPreferences: applicablePartnerPreferences,
      workspaceId: loadedMatchWorkspaceId,
      strategy: strategyProfile,
      playerRatingBasis,
      singlesPreferenceByPlayerId: Object.fromEntries(players.map((player) => [player.id, player.singles_preference || 'if_possible'])),
      sharedPlayerSchedule,
      crossTeamPolicyTeamIds,
    });
    setRecommendationAnalysis(analysis);
    setRecommendationDialogOpen(true);
    void persistRecommendationAnalysis(analysis);
  };

  const persistRecommendationAnalysis = async (analysis: RecommendationAnalysis) => {
    if (!match || !auditUserId || analysis.plans.length === 0) return;
    const { data: analysisRecord, error: analysisError } = await supabase
      .from('lineup_recommendation_analyses')
      .insert({
        trigger_type: 'manual',
        team_id: match.team_id,
        affected_match_ids: [match.id],
        strategy_profile_id: analysis.strategy.id || null,
        strategy_profile_version: analysis.strategy.version,
        advancement_type_snapshot: analysis.strategy.advancementType,
        objective_weights_snapshot: {
          competitiveness: analysis.strategy.competitivenessWeight,
          participationFairness: analysis.strategy.participationFairnessWeight,
          singlesOpportunity: analysis.strategy.singlesOpportunityWeight,
          scheduleSpacing: analysis.strategy.scheduleSpacingWeight,
          partnerCompatibility: analysis.strategy.partnerCompatibilityWeight,
          homeAwayBalance: analysis.strategy.homeAwayBalanceWeight,
        },
        singles_rating_weights_snapshot: {
          edr: analysis.strategy.singlesEdrWeight,
          singlesUtr: analysis.strategy.singlesUtrWeight,
        },
        doubles_rating_weights_snapshot: {
          edr: analysis.strategy.doublesEdrWeight,
          doublesUtr: analysis.strategy.doublesUtrWeight,
        },
        change_sensitivity_snapshot: analysis.strategy.changeSensitivity,
        tentative_player_policy_snapshot: analysis.strategy.tentativePlayerPolicy,
        generated_by: auditUserId,
      })
      .select('id')
      .single();
    if (analysisError || !analysisRecord) return;
    const analysisId = (analysisRecord as { id: string }).id;
    for (const [index, plan] of analysis.plans.entries()) {
      const { data: planRecord, error: planError } = await supabase
        .from('lineup_recommendation_plans')
        .insert({
          analysis_id: analysisId,
          plan_type: plan.planType,
          overall_score: plan.overallScore,
          objective_score_breakdown: plan.objectiveScoreBreakdown,
          disruption_penalty: plan.disruptionPenalty,
          warnings: plan.warnings,
          explanation: plan.explanations,
          rank: index + 1,
        })
        .select('id')
        .single();
      if (planError || !planRecord) continue;
      const planId = (planRecord as { id: string }).id;
      const changeRows = plan.changes.map((change) => ({
        plan_id: planId,
        match_event_id: change.matchEventId,
        line_identifier: `${change.positionCode}:${change.slotNumber}`,
        league_configuration_position_id: change.leagueConfigurationPositionId,
        slot_number: change.slotNumber,
        old_player_id: change.oldPlayerId,
        new_player_id: change.newPlayerId,
        change_type: change.changeType,
        reason: change.reason,
        score_impact: change.scoreImpact,
      }));
      if (changeRows.length > 0) {
        await supabase.from('lineup_recommendation_changes').insert(changeRows);
      }
    }
  };

  const applyRecommendationPlan = async (plan: RecommendationPlan) => {
    if (!match || !canEdit) return;
    if (match.lineup_status === 'Published' && !window.confirm('This will alter a published lineup draft. Continue?')) return;
    setSavingKey(`recommendation:${plan.id}`);
    for (const change of plan.changes.filter((item) => item.changeType !== 'unchanged')) {
      const existing = entries.find(
        (entry) =>
          entry.league_configuration_position_id === change.leagueConfigurationPositionId &&
          entry.slot_number === change.slotNumber,
      );
      if (!change.newPlayerId) {
        if (existing) {
          const { error } = await supabase
            .from('lineup_entries')
            .update({
              deleted_at: new Date().toISOString(),
              deleted_by: auditUserId || null,
              updated_by: auditUserId || null,
              delete_reason: `Removed by lineup recommendation plan: ${plan.name}`,
            })
            .eq('id', existing.id);
          if (error) {
            setSavingKey(null);
            setSnack({ severity: 'error', message: error.message });
            return;
          }
        }
        continue;
      }
      const payload = {
        match_event_id: match.id,
        league_configuration_position_id: change.leagueConfigurationPositionId,
        position_code: change.positionCode,
        slot_number: change.slotNumber,
        player_id: change.newPlayerId,
        updated_by: auditUserId || null,
        created_by: auditUserId || null,
      };
      const result = existing
        ? await supabase.from('lineup_entries').update(payload).eq('id', existing.id)
        : await supabase.from('lineup_entries').insert(payload);
      if (result.error) {
        setSavingKey(null);
        setSnack({ severity: 'error', message: result.error.message });
        return;
      }
    }
    await markDraftIfNeeded();
    await loadLineup();
    setSavingKey(null);
    setRecommendationDialogOpen(false);
    setSnack({ severity: 'success', message: `${plan.name} applied as a Draft lineup. Review before publishing.` });
  };

  const saveLineStatus = async (position: LeagueConfigurationPositionRecord, lineStatus: LineStatus) => {
    if (!match || !canEdit) return;
    const isChangingToDefaulted = defaultedLineStatuses.has(lineStatus);
    const activeEntriesForPosition = entries.filter((entry) => entry.league_configuration_position_id === position.id);
    if (
      isChangingToDefaulted &&
      (activeEntriesForPosition.length > 0 || match.lineup_status === 'Published') &&
      !window.confirm(
        `${position.position_code} will be marked ${lineStatusLabel(lineStatus)}. Active player assignments and Scheduled participation events for this line will be removed from the active lineup history. Continue?`,
      )
    ) {
      return;
    }
    setSavingKey(`line-status:${position.id}`);
    const { error } = await supabase
      .from('lineup_position_statuses')
      .upsert(
        {
          match_event_id: match.id,
          league_configuration_position_id: position.id,
          line_status: lineStatus,
        },
        { onConflict: 'match_event_id,league_configuration_position_id' },
      );

    if (error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    if (isChangingToDefaulted) {
      const deletedAt = new Date().toISOString();
      const [{ error: entriesError }, { error: participationError }] = await Promise.all([
        supabase
          .from('lineup_entries')
          .update({
            deleted_at: deletedAt,
            deleted_by: auditUserId || null,
            delete_reason: `Line marked ${lineStatusLabel(lineStatus)}`,
            updated_by: auditUserId || null,
          })
          .eq('match_event_id', match.id)
          .eq('league_configuration_position_id', position.id)
          .is('deleted_at', null),
        supabase
          .from('player_participation_events')
          .update({
            deleted_at: deletedAt,
            deleted_by: auditUserId || null,
            delete_reason: `Line marked ${lineStatusLabel(lineStatus)}`,
            updated_by: auditUserId || null,
          })
          .eq('match_event_id', match.id)
          .eq('league_configuration_position_id', position.id)
          .eq('participation_event_type', 'scheduled')
          .is('deleted_at', null),
      ]);

      if (entriesError || participationError) {
        setSavingKey(null);
        setSnack({ severity: 'error', message: entriesError?.message || participationError?.message || 'Unable to clear defaulted line assignments.' });
        return;
      }
    }

    setSavingKey(null);
    await markDraftIfNeeded();
    await loadLineup();
    setSnack({ severity: 'success', message: `${position.position_code} set to ${lineStatusLabel(lineStatus)}.` });
  };

  const parentLogistics = useMemo(() => match ? ({
    match_date: match.match_date,
    warmup_time: match.warmup_time,
    match_time: match.match_time,
    facility_id: match.facility_id,
    court_numbers: match.court_numbers,
  }) : {}, [match]);
  const logisticsForPosition = (positionId: string) => effectiveLineLogistics(parentLogistics, lineLogistics[positionId]);
  const splitSchedule = useMemo(() => matchHasSplitSchedule(Object.values(lineLogistics)), [lineLogistics]);
  const openLineLogistics = (position: LeagueConfigurationPositionRecord) => {
    setLineLogisticsPositionId(position.id);
    setLineLogisticsDraft({ ...lineLogistics[position.id] });
  };
  const closeLineLogistics = () => {
    setLineLogisticsPositionId(null);
    setLineLogisticsDraft({});
  };
  const saveLineLogistics = async () => {
    if (!match || !lineLogisticsPositionId || !canEdit) return;
    setSavingKey(`line-logistics:${lineLogisticsPositionId}`);
    const clean = (value: string | null | undefined) => value?.trim() || null;
    const payload = {
      match_event_id: match.id,
      league_configuration_position_id: lineLogisticsPositionId,
      scheduled_date_override: clean(lineLogisticsDraft.scheduled_date_override),
      warmup_time_override: clean(lineLogisticsDraft.warmup_time_override),
      start_time_override: clean(lineLogisticsDraft.start_time_override),
      facility_id_override: clean(lineLogisticsDraft.facility_id_override),
      location_text_override: clean(lineLogisticsDraft.location_text_override),
      court_assignment: clean(lineLogisticsDraft.court_assignment),
    };
    const { error } = await supabase.from('lineup_position_statuses').upsert(payload, { onConflict: 'match_event_id,league_configuration_position_id' });
    setSavingKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    await loadLineup();
    closeLineLogistics();
    setSnack({ severity: 'success', message: 'Line schedule saved.' });
  };
  const saveLineCourtAssignment = async (positionId: string, courtAssignment: string) => {
    if (!match || !canEdit) return;
    setSavingKey(`line-court:${positionId}`);
    const { error } = await supabase.from('lineup_position_statuses').upsert({
      match_event_id: match.id,
      league_configuration_position_id: positionId,
      court_assignment: courtAssignment.trim() || null,
    }, { onConflict: 'match_event_id,league_configuration_position_id' });
    setSavingKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      await loadLineup();
      return;
    }
    await loadLineup();
  };
  const assignCourts = async () => {
    if (!match || !canEdit) return;
    const start = Number(courtStartNumber);
    if (!Number.isInteger(start) || start < 1) {
      setSnack({ severity: 'error', message: 'Enter a whole court number of 1 or greater.' });
      return;
    }
    setSavingKey('assign-courts');
    const results = await Promise.all(positions.map((position, index) => supabase.from('lineup_position_statuses').upsert({
      match_event_id: match.id,
      league_configuration_position_id: position.id,
      court_assignment: String(start + index),
    }, { onConflict: 'match_event_id,league_configuration_position_id' })));
    setSavingKey(null);
    const error = results.find((result) => result.error)?.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    await loadLineup();
    setAssignCourtsOpen(false);
    setSnack({ severity: 'success', message: 'Sequential courts assigned to lineup lines.' });
  };

  const recordLineupEditHistoryIfPublished = async (playerId: string, positionId: string) => {
    if (!match || match.lineup_status !== 'Published') return;
    const { error } = await supabase.from('player_participation_events').insert({
      match_event_id: match.id,
      player_id: playerId,
      league_configuration_position_id: positionId,
      participation_event_type: 'administrative_adjustment',
      allocation_count_indicator: false,
      manual_allocation_override_indicator: false,
      participation_event_reason: 'Other',
      participation_event_notes: 'Historical lineup edit recorded from Match Lineup after publication. The corrected lineup determines actual appearances for completion-generated participation.',
      created_by: auditUserId || null,
      updated_by: auditUserId || null,
    });
    if (error && error.code !== '23505') {
      setSnack({ severity: 'error', message: error.message });
    }
  };

  const saveSlot = (slot: Slot, playerId: string) => {
    if (playerId) persistWorkspaceMove(playerId, slot);
    else {
      const occupant = entriesRef.current.find((entry) =>
        entry.league_configuration_position_id === slot.position.id && entry.slot_number === slot.slotNumber);
      if (occupant) persistWorkspaceMove(occupant.player_id);
    }
  };

  const canPlacePlayerInSlot = (player: RosterPlayer, slot: Slot, allowExistingUnavailableAssignment = false): string | null => {
    if (isDefaultedPosition(slot.position.id)) return `${slot.position.position_code} is defaulted.`;
    if (player.availability_status === 'not_available' && !allowExistingUnavailableAssignment) return `${playerName(player, userPreferences)} is marked Not Available for this match.`;
    if (slot.position.required_male_count === 1 && slot.position.required_female_count === 1 && slot.position.required_player_count === 2) {
      const expectedSex = slot.slotNumber === 1 ? 'female' : 'male';
      if (player.sex !== expectedSex) return `${lineDisplayName(slot.position)} ${slot.slotNumber === 1 ? 'left' : 'right'} slot requires a ${expectedSex === 'female' ? 'woman' : 'man'}.`;
    }
    return null;
  };

  const persistWorkspaceMove = (playerId: string, targetSlot?: Slot) => {
    if (!match || !canEdit || workspaceMovePendingRef.current) return;
    const player = playersById.get(playerId);
    if (!player) return;
    const before = entriesRef.current;
    const source = before.find((entry) => entry.player_id === playerId);
    const target = targetSlot ? before.find((entry) => entry.league_configuration_position_id === targetSlot.position.id && entry.slot_number === targetSlot.slotNumber) : undefined;
    const validationError = targetSlot ? canPlacePlayerInSlot(player, targetSlot, Boolean(source)) : null;
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return;
    }

    if ((source && target && source.id === target.id) || (!source && !targetSlot)) return;
    if (targetSlot && !source) {
      if (player.availability_status === 'maybe' && !window.confirm(`${playerName(player, userPreferences)} is marked Maybe. Assign anyway?`)) return;
      if (player.availability_status === 'unknown' && !window.confirm(`${playerName(player, userPreferences)} has Unknown availability. Assign anyway?`)) return;
      if (isDesignatedSubstitute(player) && !window.confirm(`${playerName(player, userPreferences)} is marked as a Designated Substitute. Add this player to the lineup anyway?`)) return;
    }
    if (target && !window.confirm(`Move ${playerName(player, userPreferences)} to ${targetSlot?.position.position_code} slot ${targetSlot?.slotNumber}? ${playerName(playersById.get(target.player_id), userPreferences)} will be returned to the player pool.`)) return;

    const next = optimisticWorkspaceMove(before, match.id, playerId, targetSlot && {
      positionId: targetSlot.position.id,
      positionCode: targetSlot.position.position_code,
      slotNumber: targetSlot.slotNumber,
    });

    workspaceMovePendingRef.current = true;
    entriesRef.current = next;
    setEntries(next);
    setSelectedPoolPlayerId(null);
    setLineupSaveState('saving');
    void (async () => {
      let writeCompleted = false;
      try {
        let error: { message: string } | null = null;
        if (!targetSlot && source) {
          ({ error } = await supabase.from('lineup_entries').update({ deleted_at: new Date().toISOString(), delete_reason: 'Removed from lineup workspace' }).eq('id', source.id));
        } else if (targetSlot && source && target) {
          ({ error } = await supabase.rpc('move_lineup_entry', {
            p_lineup_entry_id: source.id,
            p_target_position_id: targetSlot.position.id,
            p_target_slot_number: targetSlot.slotNumber,
            p_displace_target: true,
          }));
        } else if (targetSlot && source) {
          ({ error } = await supabase.rpc('move_lineup_entry', {
            p_lineup_entry_id: source.id,
            p_target_position_id: targetSlot.position.id,
            p_target_slot_number: targetSlot.slotNumber,
            p_displace_target: false,
          }));
        } else if (targetSlot && target) {
          ({ error } = await supabase.from('lineup_entries').update({ player_id: playerId }).eq('id', target.id));
        } else if (targetSlot) {
          ({ error } = await supabase.from('lineup_entries').insert({ match_event_id: match.id, league_configuration_position_id: targetSlot.position.id, position_code: targetSlot.position.position_code, slot_number: targetSlot.slotNumber, player_id: playerId }));
        }
        if (error) throw new Error(error.message);
        writeCompleted = true;
        await markDraftIfNeeded();
        // Reconcile temporary IDs without reloading the entire match and roster.
        const savedEntries = await refreshWorkspaceEntries();
        const persisted = targetSlot
          ? savedEntries?.some((entry) => entry.league_configuration_position_id === targetSlot.position.id && entry.slot_number === targetSlot.slotNumber && entry.player_id === playerId)
          : !savedEntries?.some((entry) => entry.id === source?.id);
        if (!persisted) {
          writeCompleted = false;
          throw new Error('The lineup change did not persist.');
        }
        if (targetSlot && targetSlot.position.required_player_count > 1 && targetSlot.position.line_type !== 'singles') {
          setActivePartnerContext({ playerId, positionId: targetSlot.position.id });
        }
        if (targetSlot || source) await recordLineupEditHistoryIfPublished(playerId, targetSlot?.position.id || source!.league_configuration_position_id);
        setLineupSaveState('saved');
        void refreshWorkspaceParticipation().catch(() => {
          setSnack({ severity: 'warning', message: 'Lineup saved, but participation totals could not refresh. Reload to update them.' });
        });
      } catch (error) {
        try { await refreshWorkspaceEntries(); }
        catch {
          try { await loadLineup(); }
          catch {
            entriesRef.current = before;
            setEntries(before);
          }
        }
        setLineupSaveState('error');
        const reason = error instanceof Error ? error.message : 'Unknown error';
        setSnack({ severity: 'error', message: writeCompleted
          ? `Lineup saved, but the latest assignments could not be confirmed: ${reason}`
          : `Unable to save lineup move: ${reason}` });
      } finally {
        workspaceMovePendingRef.current = false;
      }
    })();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleWorkspaceDragStart = (event: DragStartEvent) => {
    const playerId = event.active.data.current?.playerId as string || null;
    setActiveDragPlayerId(playerId);
  };

  const handleWorkspaceDragEnd = (event: DragEndEvent) => {
    setActiveDragPlayerId(null);
    const playerId = event.active.data.current?.playerId as string | undefined;
    const dropId = event.over ? String(event.over.id) : null;
    if (!playerId || !dropId) {
      setSnack({ severity: 'warning', message: 'Drop a player directly on a lineup slot.' });
      return;
    }
    if (dropId === 'pool:remove') {
      persistWorkspaceMove(playerId);
      return;
    }
    const [prefix, positionId, slotNumber] = dropId.split(':');
    if (prefix !== 'slot') return;
    const position = positions.find((item) => item.id === positionId);
    if (!position || !Number(slotNumber)) return;
    persistWorkspaceMove(playerId, { position, slotNumber: Number(slotNumber) });
  };

  const clearLine = async (position: LeagueConfigurationPositionRecord) => {
    if (!match || !canEdit) return;
    const activeEntriesForPosition = entries.filter((entry) => entry.league_configuration_position_id === position.id);
    if (activeEntriesForPosition.length === 0) return;

    setSavingKey(`clear-line:${position.id}`);
    const deletedAt = new Date().toISOString();
    const [{ error: entriesError }, { error: participationError }] = await Promise.all([
      supabase
        .from('lineup_entries')
        .update({
          deleted_at: deletedAt,
          deleted_by: auditUserId || null,
          delete_reason: 'Line cleared from Match Lineup',
          updated_by: auditUserId || null,
        })
        .eq('match_event_id', match.id)
        .eq('league_configuration_position_id', position.id)
        .is('deleted_at', null),
      supabase
        .from('player_participation_events')
        .update({
          deleted_at: deletedAt,
          deleted_by: auditUserId || null,
          delete_reason: 'Line cleared from Match Lineup',
          updated_by: auditUserId || null,
        })
        .eq('match_event_id', match.id)
        .eq('league_configuration_position_id', position.id)
        .eq('participation_event_type', 'scheduled')
        .is('deleted_at', null),
    ]);

    setSavingKey(null);
    if (entriesError || participationError) {
      setSnack({ severity: 'error', message: entriesError?.message || participationError?.message || 'Unable to clear line.' });
      return;
    }

    await markDraftIfNeeded();
    await loadLineup();
    setSnack({ severity: 'success', message: `${position.position_code} cleared.` });
  };

  const closeLineMenu = () => {
    setLineMenuAnchor(null);
    setLineMenuPositionId(null);
  };

  const openLineMenu = (event: MouseEvent<HTMLElement>, positionId: string) => {
    setLineMenuAnchor(event.currentTarget);
    setLineMenuPositionId(positionId);
  };

  const swapLinePlayers = async (position: LeagueConfigurationPositionRecord) => {
    if (!match || !canEdit || position.required_player_count < 2) return;
    const firstEntry = entryBySlot.get(slotKey(position.id, 1));
    const secondEntry = entryBySlot.get(slotKey(position.id, 2));
    if (!firstEntry || !secondEntry) {
      setSnack({ severity: 'warning', message: 'Both doubles players must be assigned before swapping.' });
      return;
    }
    if (isDefaultedPosition(position.id)) {
      setSnack({ severity: 'error', message: `${position.position_code} is marked ${lineStatusLabel(lineStatusForPosition(position.id))}. Set it to Normal before swapping players.` });
      return;
    }

    setSavingKey(`swap-line:${position.id}`);
    const deletedAt = new Date().toISOString();
    const clearFirst = await supabase
      .from('lineup_entries')
      .update({
        deleted_at: deletedAt,
        deleted_by: auditUserId || null,
        updated_by: auditUserId || null,
        delete_reason: 'Temporarily cleared while swapping doubles players',
      })
      .eq('id', firstEntry.id);
    if (clearFirst.error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: clearFirst.error.message });
      return;
    }

    const updateSecond = await supabase
      .from('lineup_entries')
      .update({
        player_id: firstEntry.player_id,
        updated_by: auditUserId || null,
      })
      .eq('id', secondEntry.id);
    if (updateSecond.error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: updateSecond.error.message });
      await loadLineup();
      return;
    }

    const restoreFirst = await supabase
      .from('lineup_entries')
      .update({
        player_id: secondEntry.player_id,
        deleted_at: null,
        deleted_by: null,
        delete_reason: null,
        updated_by: auditUserId || null,
      })
      .eq('id', firstEntry.id);
    if (restoreFirst.error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: restoreFirst.error.message });
      await loadLineup();
      return;
    }

    if (match.lineup_status === 'Published') {
      await recordLineupEditHistoryIfPublished(secondEntry.player_id, position.id);
      await recordLineupEditHistoryIfPublished(firstEntry.player_id, position.id);
    }
    await markDraftIfNeeded();
    await loadLineup();
    setSavingKey(null);
    setSnack({ severity: 'success', message: `${position.position_code} players swapped.` });
  };

  const clearEntireLineup = async () => {
    if (!match || !canEdit || entries.length === 0) return;
    if (!window.confirm('Are you sure you want to clear the entire lineup?')) return;

    setSavingKey('clear-lineup');
    const deletedAt = new Date().toISOString();
    const [{ error: entriesError }, { error: participationError }] = await Promise.all([
      supabase
        .from('lineup_entries')
        .update({
          deleted_at: deletedAt,
          deleted_by: auditUserId || null,
          delete_reason: 'Entire lineup cleared from Match Lineup',
          updated_by: auditUserId || null,
        })
        .eq('match_event_id', match.id)
        .is('deleted_at', null),
      supabase
        .from('player_participation_events')
        .update({
          deleted_at: deletedAt,
          deleted_by: auditUserId || null,
          delete_reason: 'Entire lineup cleared from Match Lineup',
          updated_by: auditUserId || null,
        })
        .eq('match_event_id', match.id)
        .eq('participation_event_type', 'scheduled')
        .is('deleted_at', null),
    ]);

    setSavingKey(null);
    if (entriesError || participationError) {
      setSnack({ severity: 'error', message: entriesError?.message || participationError?.message || 'Unable to clear lineup.' });
      return;
    }

    await markDraftIfNeeded();
    await loadLineup();
    setSnack({ severity: 'success', message: 'Lineup cleared.' });
  };

  const openCancellationDialog = (entry: LineupEntryRecord) => {
    setCancellationForm({
      entryId: entry.id,
      cancellationType: 'cancelled_late',
      reason: 'Injury',
      replacementPlayerId: '',
      notes: '',
      cancellationCountsTowardAllocation: true,
      manualAllocationOverride: true,
      replacementAllocationOverride: false,
    });
    setCancellationDialogOpen(true);
  };

  const saveParticipationEvent = async (payload: ParticipationEventPayload): Promise<{ data: { id: string } | null; error: { message: string } | null }> => {
    const existingResult = await supabase
      .from('player_participation_events')
      .select('id')
      .eq('match_event_id', payload.match_event_id)
      .eq('player_id', payload.player_id)
      .eq('league_configuration_position_id', payload.league_configuration_position_id)
      .eq('participation_event_type', payload.participation_event_type)
      .is('deleted_at', null)
      .maybeSingle();

    if (existingResult.error) {
      return { data: null, error: { message: existingResult.error.message } };
    }

    if (existingResult.data) {
      const { data, error } = await supabase
        .from('player_participation_events')
        .update({
          ...payload,
          updated_by: auditUserId || null,
        })
        .eq('id', (existingResult.data as { id: string }).id)
        .select('id')
        .single();
      return { data: (data as { id: string } | null) || null, error: error ? { message: error.message } : null };
    }

    const { data, error } = await supabase.from('player_participation_events').insert(payload).select('id').single();
    return { data: (data as { id: string } | null) || null, error: error ? { message: error.message } : null };
  };

  const submitCancellation = async () => {
    if (!match || !selectedCancellationEntry || !selectedCancellationPlayer || !selectedCancellationPosition) return;
    if (!canRecordCancellation) {
      setSnack({ severity: 'error', message: 'Cancellation participation events are only recorded after a lineup is Published.' });
      return;
    }
    if (!auditUserId) {
      setSnack({ severity: 'error', message: 'Cannot record cancellation because no default app user is configured.' });
      return;
    }
    const replacementPlayer = cancellationForm.replacementPlayerId ? playersById.get(cancellationForm.replacementPlayerId) : undefined;
    if (cancellationForm.replacementPlayerId && !replacementPlayer) {
      setSnack({ severity: 'error', message: 'Replacement player must be an active rostered player for this team.' });
      return;
    }
    if (replacementPlayer?.availability_status === 'maybe' && !window.confirm(`${playerName(replacementPlayer, userPreferences)} is marked Maybe. Use as replacement anyway?`)) return;
    if (replacementPlayer?.availability_status === 'unknown' && !window.confirm(`${playerName(replacementPlayer, userPreferences)} has Unknown availability. Use as replacement anyway?`)) return;
    const replacementConflict = replacementPlayer ? sameDayConflictForPlayer(replacementPlayer.id) : undefined;
    if (replacementPlayer && replacementConflict && !window.confirm(`${playerName(replacementPlayer, userPreferences)} is ${sameDayConflictLabel(replacementConflict)}. Use as replacement anyway?`)) return;
    if (replacementPlayer && assignedLabelForPlayer(replacementPlayer.id, selectedCancellationEntry.id)) {
      setSnack({ severity: 'error', message: 'Replacement player is already assigned elsewhere in this official lineup.' });
      return;
    }
    if (replacementPlayer && (selectedCancellationPosition.required_male_count > 0 || selectedCancellationPosition.required_female_count > 0)) {
      const positionPlayers = entries
        .filter((entry) => entry.league_configuration_position_id === selectedCancellationPosition.id)
        .map((entry) => (entry.id === selectedCancellationEntry.id ? replacementPlayer : playersById.get(entry.player_id)))
        .filter(Boolean) as RosterPlayer[];
      const maleCount = positionPlayers.filter((player) => player.sex === 'male').length;
      const femaleCount = positionPlayers.filter((player) => player.sex === 'female').length;
      if (maleCount !== selectedCancellationPosition.required_male_count || femaleCount !== selectedCancellationPosition.required_female_count) {
        setSnack({ severity: 'error', message: `${selectedCancellationPosition.position_code} requires ${selectedCancellationPosition.required_male_count} male and ${selectedCancellationPosition.required_female_count} female player${selectedCancellationPosition.required_player_count === 1 ? '' : 's'}.` });
        return;
      }
    }

    setSavingKey(`cancel:${selectedCancellationEntry.id}`);
    const scheduledResult = await supabase
      .from('player_participation_events')
      .select('id')
      .eq('match_event_id', match.id)
      .eq('player_id', selectedCancellationEntry.player_id)
      .eq('league_configuration_position_id', selectedCancellationEntry.league_configuration_position_id)
      .eq('participation_event_type', 'scheduled')
      .is('deleted_at', null)
      .maybeSingle();

    if (scheduledResult.error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: scheduledResult.error.message });
      return;
    }

    const notes = cancellationForm.notes.trim() || null;
    const cancellationResult = await saveParticipationEvent({
      match_event_id: match.id,
      player_id: selectedCancellationEntry.player_id,
      league_configuration_position_id: selectedCancellationEntry.league_configuration_position_id,
      participation_event_type: cancellationForm.cancellationType,
      related_player_participation_event_id: (scheduledResult.data as { id?: string } | null)?.id || null,
      allocation_count_indicator: cancellationForm.cancellationCountsTowardAllocation,
      manual_allocation_override_indicator: true,
      participation_event_reason: cancellationForm.reason,
      participation_event_notes: notes ? `Published lineup cancellation workflow. ${notes}` : 'Published lineup cancellation workflow.',
      replaced_player_id: selectedCancellationEntry.player_id,
      replacement_player_id: cancellationForm.replacementPlayerId || null,
      created_by: auditUserId,
      updated_by: auditUserId,
    });

    if (cancellationResult.error) {
      setSavingKey(null);
      setSnack({ severity: 'error', message: cancellationResult.error.message });
      return;
    }

    if (cancellationForm.replacementPlayerId) {
      const [{ error: lineupError }, { error: replacementError }] = await Promise.all([
        supabase
          .from('lineup_entries')
          .update({
            player_id: cancellationForm.replacementPlayerId,
            updated_by: auditUserId,
          })
          .eq('id', selectedCancellationEntry.id),
        saveParticipationEvent({
          match_event_id: match.id,
          player_id: cancellationForm.replacementPlayerId,
          league_configuration_position_id: selectedCancellationEntry.league_configuration_position_id,
          participation_event_type: 'late_substitute',
          related_player_participation_event_id: cancellationResult.data?.id || null,
          allocation_count_indicator: cancellationForm.replacementAllocationOverride,
          manual_allocation_override_indicator: cancellationForm.replacementAllocationOverride,
          participation_event_reason: cancellationForm.reason,
          participation_event_notes: notes ? `Replacement for cancellation. ${notes}` : `Replacement for ${playerName(selectedCancellationPlayer, userPreferences)} cancellation.`,
          replaced_player_id: selectedCancellationEntry.player_id,
          replacement_player_id: cancellationForm.replacementPlayerId,
          created_by: auditUserId,
          updated_by: auditUserId,
        }),
      ]);
      if (lineupError || replacementError) {
        setSavingKey(null);
        setSnack({ severity: 'error', message: lineupError?.message || replacementError?.message || 'Unable to save replacement.' });
        return;
      }
    } else {
      const { error: removeError } = await supabase
        .from('lineup_entries')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: auditUserId,
          delete_reason: `${participationCancellationTypeOptions.find((option) => option.value === cancellationForm.cancellationType)?.label || 'Cancellation'} by ${playerName(selectedCancellationPlayer, userPreferences)}`,
          updated_by: auditUserId,
        })
        .eq('id', selectedCancellationEntry.id);
      if (removeError) {
        setSavingKey(null);
        setSnack({ severity: 'error', message: removeError.message });
        return;
      }
    }

    setSavingKey(null);
    setCancellationDialogOpen(false);
    lineupDraftPersistence.clearDraft();
    await loadLineup();
    setSnack({
      severity: 'success',
      message: cancellationForm.replacementPlayerId
        ? 'Cancellation and replacement recorded.'
        : 'Cancellation recorded. The slot needs replacement.',
    });
  };

  const addStandby = async () => {
    if (!match || !standbyPlayerId || !canEdit) return;
    if (standbyPlayerIds.has(standbyPlayerId)) {
      setSnack({ severity: 'error', message: 'This player is already listed as standby for this match.' });
      return;
    }
    const player = playersById.get(standbyPlayerId);
    if (player?.availability_status === 'maybe' && !window.confirm(`${playerName(player, userPreferences)} is marked Maybe. Add as standby anyway?`)) return;
    if (player?.availability_status === 'unknown' && !window.confirm(`${playerName(player, userPreferences)} has Unknown availability. Add as standby anyway?`)) return;
    if (player && isDesignatedSubstitute(player) && !window.confirm(`${playerName(player, userPreferences)} is marked as a Designated Substitute. Add as standby anyway?`)) return;
    const { error } = await supabase.from('standby_players').insert({
      match_event_id: match.id,
      player_id: standbyPlayerId,
      notes: standbyNotes.trim() || null,
    });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setStandbyPlayerId('');
    setStandbyNotes('');
    lineupDraftPersistence.clearDraft();
    await markDraftIfNeeded();
    await loadLineup();
    setSnack({ severity: 'success', message: 'Standby player added.' });
  };

  const removeStandby = async (standby: StandbyPlayerRecord) => {
    if (!canEdit) return;
    const { error } = await supabase
      .from('standby_players')
      .update({ deleted_at: new Date().toISOString(), delete_reason: 'Removed from Phase 4 Lineup Builder' })
      .eq('id', standby.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    await loadLineup();
  };

  const publishLineup = async (options: { copyInternal?: boolean } = {}) => {
    if (!match) return false;
    if (!auditUserId) {
      setSnack({ severity: 'error', message: 'Cannot publish lineup because no default app user is configured.' });
      return false;
    }
    if (incompletePositions.length > 0 || genderWarnings.length > 0 || duplicateAssignments.length > 0) {
      setSnack({ severity: 'error', message: 'Complete and fix lineup validation warnings before publishing.' });
      return false;
    }
    if (pairingRuleValidation.hardErrors.length > 0) {
      setSnack({ severity: 'error', message: pairingRuleValidation.hardErrors[0] });
      return false;
    }
    const { data, error } = await supabase.rpc('publish_match_lineup', { p_match_event_id: match.id, p_published_by: auditUserId });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return false;
    }
    if (options.copyInternal) {
      try {
        await navigator.clipboard.writeText(lineupText(false));
      } catch {
        setSnack({ severity: 'warning', message: 'Lineup published, but CourtCaptain could not copy it to the clipboard.' });
        setMatch(data as unknown as MatchEventRecord);
        lineupDraftPersistence.clearDraft();
        await loadLineup();
        return true;
      }
    }
    setMatch(data as unknown as MatchEventRecord);
    lineupDraftPersistence.clearDraft();
    await loadLineup();
    setSnack({
      severity: 'success',
      message: options.copyInternal ? 'Lineup published and copied to clipboard.' : 'Lineup published.',
    });
    return true;
  };

  const moveLineupToDraft = async () => {
    if (!match) return null;
    if (!auditUserId) {
      setSnack({ severity: 'error', message: 'Cannot move lineup to Draft because no default app user is configured.' });
      return;
    }
    if (match.lineup_status !== 'Published') {
      setSnack({ severity: 'error', message: 'Only Published lineups can be moved back to Draft.' });
      return;
    }
    if (!window.confirm('Move this published lineup back to Draft? Scheduled participation records from this publication will be removed and the lineup can be edited before republishing.')) return;

    const movedAt = new Date().toISOString();
    const [{ data, error: matchError }, { error: participationError }] = await Promise.all([
      supabase
        .from('match_events')
        .update({
          lineup_status: 'Draft',
          lineup_published_at: null,
          lineup_published_by: null,
          updated_by: auditUserId,
        })
        .eq('id', match.id)
        .select('*')
        .single(),
      supabase
        .from('player_participation_events')
        .update({
          deleted_at: movedAt,
          deleted_by: auditUserId,
          delete_reason: 'Lineup moved back to Draft',
          updated_by: auditUserId,
        })
        .eq('match_event_id', match.id)
        .eq('participation_event_type', 'scheduled')
        .is('deleted_at', null),
    ]);

    if (matchError || participationError) {
      setSnack({ severity: 'error', message: matchError?.message || participationError?.message || 'Unable to move lineup to Draft.' });
      return;
    }

    setMatch(data as unknown as MatchEventRecord);
    lineupDraftPersistence.clearDraft();
    await loadLineup();
    setSnack({ severity: 'success', message: 'Lineup moved back to Draft.' });
  };

  const overridePairingRulesForMatch = async () => {
    if (!match || !auditUserId || hardPairingViolationPreferences.length === 0) return;
    const reason = window.prompt('Why are you overriding these partner scheduling preferences for this match?', 'Captain-approved lineup exception');
    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSnack({ severity: 'error', message: 'An override reason is required.' });
      return;
    }
    const payload = hardPairingViolationPreferences.map((preference) => ({
      match_event_id: match.id,
      partner_preference_id: preference.id,
      pairing_rule: normalizePairingRule(preference.pairing_rule),
      override_reason: trimmedReason,
      created_by: auditUserId,
      updated_by: auditUserId,
    }));
    for (const override of payload) {
      const existing = pairingRuleOverrides.find((item) => item.partner_preference_id === override.partner_preference_id);
      const result = existing
        ? await supabase.from('lineup_pairing_rule_overrides').update({ ...override, updated_at: new Date().toISOString() }).eq('id', existing.id)
        : await supabase.from('lineup_pairing_rule_overrides').insert(override);
      if (result.error) {
        setSnack({ severity: 'error', message: result.error.message });
        return;
      }
    }
    await supabase.from('match_lifecycle_history').insert({
      match_event_id: match.id,
      previous_lifecycle_state: match.lineup_status,
      new_lifecycle_state: match.lineup_status,
      changed_at: new Date().toISOString(),
      changed_by: auditUserId,
      change_reason: `Partner scheduling preference override: ${trimmedReason}`,
    });
    await loadLineup();
    setSnack({ severity: 'success', message: 'Scheduling preference override recorded for this match.' });
  };

  const lineupText = (external: boolean): string => {
    const lines = [
      external ? 'External Lineup' : 'Internal Lineup',
      formatMatch(match, lookups, timeFormat),
      ...(!external && match?.lineup_status === 'Draft' ? ['DRAFT LINEUP - not yet published.'] : []),
      '',
      ...positions.map((position) => {
        const lineStatus = lineStatusForPosition(position.id);
        if (defaultedLineStatuses.has(lineStatus)) {
          return `${position.position_code}: ${lineStatusLabel(lineStatus)}`;
        }
        const names = entries
          .filter((entry) => entry.league_configuration_position_id === position.id)
          .sort((a, b) => a.slot_number - b.slot_number)
          .map((entry) => formatPlayerFirstLast(playersById.get(entry.player_id)))
          .filter(Boolean)
          .join(' / ');
        return `${position.position_code}: ${names || 'TBD'}`;
      }),
    ];
    if (!external && standbyPlayers.length > 0) {
      lines.push('', 'Standby:', ...standbyPlayers.map((standby) => `- ${formatPlayerFirstLast(playersById.get(standby.player_id))}${standby.notes ? ` (${standby.notes})` : ''}`));
    }
    return lines.join('\n');
  };

  const copyLineup = async (external: boolean) => {
    await navigator.clipboard.writeText(lineupText(external));
    setSnack({ severity: 'success', message: `${external ? 'External' : 'Internal'} lineup copied.` });
  };

  const openScoresheetBlob = (blob: Blob, filename: string, popup: Window | null, mode: 'preview' | 'download') => {
    const pdfFile = new File([blob], filename, { type: 'application/pdf' });
    const url = window.URL.createObjectURL(pdfFile);
    if (mode === 'preview' && popup) {
      popup.location.href = url;
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const fetchTcdScoresheetPdf = async (confirmedWarnings = false): Promise<{ blob: Blob; filename: string } | null> => {
    if (!match) return null;
    if (!confirmedWarnings && lineupDraftPersistence.isDirty) {
      const proceed = window.confirm('You have unsaved lineup changes. The TCD scoresheet will use the last saved lineup, not the unsaved draft on this screen. Continue?');
      if (!proceed) return null;
    }

    const sessionResult = await supabase.auth.getSession();
    const accessToken = sessionResult.data.session?.access_token;
    if (!accessToken) throw new Error('Please log in again before generating the scoresheet.');

    const response = await fetch('/api/scoresheets/tcd-mixed', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        matchId: match.id,
        nameFormat: userPreferences.playerNameDisplayFormat,
        confirmedWarnings,
      }),
    });

    if (response.status === 409) {
      const warningBody = await response.json().catch(() => ({ warnings: [] }));
      const warnings: string[] = Array.isArray(warningBody.warnings) ? warningBody.warnings.map((warning: unknown) => String(warning)) : [];
      const proceed = window.confirm(`Generate this scoresheet with the saved lineup?\n\n${warnings.map((warning) => `- ${warning}`).join('\n')}`);
      return proceed ? fetchTcdScoresheetPdf(true) : null;
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.error || 'Unable to generate TCD scoresheet.');
    }

    const disposition = response.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="([^"]+)"/i);
    const filename = filenameMatch?.[1] || 'tcd-mixed-scoresheet.pdf';
    return { blob: await response.blob(), filename };
  };

  const generateTcdScoresheet = async (mode: 'preview' | 'download' = 'preview') => {
    const popup = mode === 'preview' ? window.open('', '_blank') : null;
    setScoresheetGenerating(true);
    try {
      const result = await fetchTcdScoresheetPdf();
      if (!result) {
        popup?.close();
        return;
      }
      const { blob, filename } = result;
      openScoresheetBlob(blob, filename, popup, mode);
      setSnack({ severity: 'success', message: 'TCD scoresheet generated.' });
    } catch (error) {
      popup?.close();
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to generate TCD scoresheet.' });
    } finally {
      setScoresheetGenerating(false);
    }
  };

  const healthMetricCards = (metrics: LineupHealthMetric[]) => metrics.map((item) => (
    <Paper key={item.key} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
          <Chip size="small" color={healthSeverityColor[item.severity]} label={item.rating} />
        </Stack>
        <LinearProgress variant="determinate" value={item.score} color={healthSeverityColor[item.severity]} sx={{ height: 7, borderRadius: 1 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" color="text.secondary">{item.detail}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{item.score}</Typography>
        </Stack>
      </Stack>
    </Paper>
  ));

  const lineupHealthDetails = (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
          {lineupHealth.overallScore}
        </Typography>
        <Typography variant="body2" color={`${healthSeverityColor[severityForHealthScore(lineupHealth.overallScore)]}.main`} sx={{ fontWeight: 700 }}>
          {lineupHealth.overallRating}
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: '1fr' }, gap: 1 }}>
        {healthMetricCards(lineupHealth.metrics)}
      </Box>
      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Recommended Improvements
        </Typography>
        <Stack spacing={0.75}>
          {lineupHealth.recommendations.map((item) => (
            <Stack key={item.key} direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ color: `${healthSeverityColor[item.severity]}.main`, display: 'flex', pt: 0.15 }}>
                {healthSeverityIcon(item.severity)}
              </Box>
              <Typography variant="body2">{item.message}</Typography>
            </Stack>
          ))}
          {lineupHealth.recommendations.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Complete the lineup to see recommendations.
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );

  const lineupHealthPanel = (
    <Box sx={{ borderLeft: { xs: 0, sm: '1px solid' }, borderColor: 'divider', pl: { xs: 0, sm: 1.25 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
            Lineup Health
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>{lineupHealth.overallScore} / 100</Typography>
            <Typography variant="body2" color={`${healthSeverityColor[severityForHealthScore(lineupHealth.overallScore)]}.main`} sx={{ fontWeight: 800 }}>
              {lineupHealth.overallRating}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lineupHealth.recommendations.length} Issue{lineupHealth.recommendations.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip color={healthSeverityColor[severityForHealthScore(lineupHealth.overallScore)]} label={`${lineupHealth.overallScore} / 100`} />
          <Tooltip title={lineupHealthOpen ? 'Close Lineup Health details' : 'Open Lineup Health details'}>
            <IconButton
              size="small"
              aria-label={lineupHealthOpen ? 'Close Lineup Health details' : 'Open Lineup Health details'}
              aria-expanded={lineupHealthOpen ? 'true' : 'false'}
              onClick={(event) => setLineupHealthAnchor(lineupHealthOpen ? null : event.currentTarget)}
            >
              {lineupHealthOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );

  const hasValidationIssues = incompletePositions.length > 0 || genderWarnings.length > 0 || duplicateAssignments.length > 0 || pairingRuleValidation.hardErrors.length > 0;
  const isPublished = match?.lineup_status === 'Published';
  const isCompleted = match?.match_status === 'Completed';
  const isTcdScoresheetFormat = positions.length === 4
    && positions.every((position) => position.is_doubles && Number(position.required_player_count) === 2 && /doubles/i.test(position.line_type || ''));
  const hasScoresheetSide = match?.match_site_type === 'Home' || match?.match_site_type === 'Away';
  const canGenerateTcdScoresheet = Boolean(match && isTcdScoresheetFormat && hasScoresheetSide && entries.length > 0);
  const scoresheetDisabledReason = !match
    ? 'Load a match first.'
    : !isTcdScoresheetFormat
      ? 'TCD scoresheets are available for four-doubles formats only.'
      : !hasScoresheetSide
        ? 'Set the match site to Home or Away first.'
        : entries.length === 0
          ? 'Save at least one lineup player first.'
          : '';
  const showPublishAction = Boolean(match && !isPublished && !isCompleted);
  const showSuggestAction = Boolean(match && !isPublished && !isCompleted);
  const statusChipLabel = lineupStatusOptions.find((option) => option.value === match?.lineup_status)?.label || match?.lineup_status || 'Not Started';
  const statusChipColor: 'default' | 'success' | 'warning' = isPublished ? 'success' : match?.lineup_status === 'Draft' ? 'warning' : 'default';
  const canPublish = Boolean(match && !isPublished && !isCompleted && auditUserId && !hasValidationIssues);
  const canMoveToDraft = Boolean(match && isPublished && !isCompleted && auditUserId);
  const canSuggest = Boolean(canEdit && strategyProfile && !loading && !isCompleted);

  const historyRows = useMemo(() => {
    const rows: Array<{ label: string; timestamp: string | null | undefined; actor?: string | null; detail?: string | null }> = [
      { label: 'Created', timestamp: match?.created_at, actor: match?.created_by },
      { label: 'Updated', timestamp: match?.updated_at, actor: match?.updated_by },
      { label: 'Published', timestamp: match?.lineup_published_at, actor: match?.lineup_published_by },
      { label: 'Locked', timestamp: match?.lineup_locked_at, actor: match?.lineup_locked_by },
      ...lifecycleHistory.map((row) => ({
        label: row.previous_lifecycle_state ? `${row.previous_lifecycle_state} to ${row.new_lifecycle_state}` : row.new_lifecycle_state,
        timestamp: row.changed_at,
        actor: row.changed_by,
        detail: row.change_reason,
      })),
    ];
    return rows
      .filter((row) => row.timestamp)
      .sort((a, b) => new Date(b.timestamp || '').getTime() - new Date(a.timestamp || '').getTime());
  }, [lifecycleHistory, match?.created_at, match?.created_by, match?.lineup_locked_at, match?.lineup_locked_by, match?.lineup_published_at, match?.lineup_published_by, match?.updated_at, match?.updated_by]);

  const publishDisabledReason = !auditUserId
    ? 'No default app user is configured.'
    : hasValidationIssues
      ? 'Complete and fix lineup validation warnings before publishing.'
      : isCompleted
        ? 'Completed matches cannot be published from this action bar.'
        : isPublished
          ? 'This lineup has already moved past publishing.'
          : '';

  const moveToDraftDisabledReason = !auditUserId
    ? 'No default app user is configured.'
    : isCompleted
      ? 'Completed matches cannot be moved back to Draft.'
      : !isPublished
        ? 'Only Published lineups can be moved back to Draft.'
        : '';

  const suggestDisabledReason = !canEdit
    ? 'This lineup cannot currently be edited.'
    : !strategyProfile
      ? 'No recommendation strategy is available.'
      : loading
        ? 'Lineup is still loading.'
        : isCompleted
            ? 'Completed matches do not need lineup suggestions.'
            : '';

  const activeLineMenuPosition = positions.find((position) => position.id === lineMenuPositionId) || null;

  const lineSlots = (position: LeagueConfigurationPositionRecord): Slot[] =>
    Array.from({ length: position.required_player_count }, (_, index) => ({ position, slotNumber: index + 1 }));

  const lineEntries = (positionId: string): LineupEntryRecord[] =>
    entries.filter((entry) => entry.league_configuration_position_id === positionId).sort((a, b) => a.slot_number - b.slot_number);

  const linePlayers = (position: LeagueConfigurationPositionRecord): Array<RosterPlayer | undefined> =>
    lineSlots(position).map((slot) => {
      const entry = entryBySlot.get(slotKey(position.id, slot.slotNumber));
      return entry ? playersById.get(entry.player_id) : undefined;
    });

  const lineWarningMessages = (position: LeagueConfigurationPositionRecord): string[] => {
    const messages = new Set<string>();
    lineEntries(position.id).forEach((entry) => {
      const player = playersById.get(entry.player_id);
      if (!player) return;
      if (player.availability_status === 'maybe') messages.add(`${playerName(player, userPreferences)} is tentative.`);
      if (player.availability_status === 'unknown') messages.add(`${playerName(player, userPreferences)} has unknown availability.`);
      if (player.availability_status === 'not_available') messages.add(`${playerName(player, userPreferences)} is unavailable.`);
      if (duplicatePlayerIds.has(player.id)) messages.add(`${playerName(player, userPreferences)} is assigned more than once.`);
      const conflict = sameDayConflictForPlayer(player.id);
      if (conflict) messages.add(`${playerName(player, userPreferences)} is ${sameDayConflictLabel(conflict)}.`);
    });
    if (position.required_player_count > 1) {
      const selected = lineEntries(position.id);
      if (selected.length >= 2) {
        const detail = partnerCompatibility(selected[0].player_id, selected[1].player_id);
        if (detail.pairingRule !== 'none') {
          messages.add(`Scheduling preference: ${pairingRuleLabels[detail.pairingRule]}.`);
        }
        if (detail.pairingRule === 'excluded') {
          messages.add('This partnership has a Do Not Pair scheduling preference.');
        } else if (detail.pairingRule === 'avoid_pairing') {
          messages.add('This partnership has an Avoid Pairing scheduling preference.');
        }
        if (detail.compatibility === 'Poor') {
          messages.add('This partnership has Poor compatibility.');
        }
      }
    }
    return Array.from(messages);
  };

  const renderPlayerSelect = (slot: Slot, entry: LineupEntryRecord | undefined, selectedPlayer: RosterPlayer | undefined, isDefaultedLine: boolean) => {
    const key = slotKey(slot.position.id, slot.slotNumber);
    if (isDefaultedLine) {
      return (
        <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default', minHeight: 112 }}>
          <Typography variant="body2" color="text.secondary">No player assignment required.</Typography>
        </Paper>
      );
    }

    return (
      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          value={entry?.player_id || ''}
          disabled={!canEdit || savingKey === key}
          onChange={(event) => void saveSlot(slot, event.target.value)}
          renderValue={() => (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {slot.position.required_player_count > 1 ? `Player ${slot.slotNumber}` : 'Player'}
              </Typography>
              {selectedPlayer ? (
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>
                      {playerName(selectedPlayer, userPreferences)}
                    </Typography>
                    <Chip size="small" color={availabilityChipColor(selectedPlayer.availability_status)} label={availabilityLabels[selectedPlayer.availability_status]} />
                    {selectedPlayer.player_tier && <Chip size="small" variant="outlined" label={tierLabel(selectedPlayer.player_tier)} />}
                    {isDesignatedSubstitute(selectedPlayer) && <Chip size="small" variant="outlined" color="info" label="Substitute" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {playerRatingSummary(selectedPlayer, slot.position.line_type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {playerWorkloadSummary(selectedPlayer)}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.75}>
                  <Typography variant="body1" sx={{ fontWeight: 800 }}>Select player</Typography>
                  <Typography variant="body2" color="text.secondary">Required</Typography>
                </Stack>
              )}
            </Box>
          )}
          sx={{
            minHeight: 112,
            alignItems: 'stretch',
            bgcolor: 'background.default',
            cursor: canEdit ? 'pointer' : 'default',
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'stretch',
              whiteSpace: 'normal',
              py: 1.25,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: canEdit ? 'primary.main' : undefined,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          }}
        >
          <MenuItem value="">Unassigned</MenuItem>
          {groupedPlayers.flatMap((group) => [
            <ListSubheader key={`line-${slot.position.id}-${slot.slotNumber}-${group.tier || 'roster'}`}>{group.label}</ListSubheader>,
            ...group.players.map((player) => {
              const assignedLabel = assignedLabelForPlayer(player.id, entry?.id);
              const sameDayConflict = sameDayConflictForPlayer(player.id);
              const partnerIds = slotPartnerIds(slot.position.id, slot.slotNumber, entry?.id);
              const compatibilityDetail = partnerIds.map((partnerId) => partnerCompatibility(player.id, partnerId))[0];
              return (
                <MenuItem key={player.id} value={player.id}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{playerDisplay(player, userPreferences)}</Typography>
                      {isDesignatedSubstitute(player) && <Chip size="small" variant="outlined" color="info" label="Substitute" />}
                      {compatibilityDetail && (
                        <Chip size="small" color={compatibilityChipColor(compatibilityDetail.compatibility)} label={compatibilityDetail.compatibility} />
                      )}
                      {compatibilityDetail && compatibilityDetail.pairingRule !== 'none' && (
                        <Chip size="small" color={pairingRuleChipColor(compatibilityDetail.pairingRule)} label={pairingRuleLabels[compatibilityDetail.pairingRule]} />
                      )}
                      {sameDayConflict && (
                        <Tooltip title={sameDayConflictLabel(sameDayConflict)}>
                          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {playerWorkloadSummary(player)}
                    </Typography>
                    {compatibilityDetail?.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {compatibilityDetail.notes}
                      </Typography>
                    )}
                    {compatibilityDetail && compatibilityDetail.pairingRule !== 'none' && (
                      <Typography variant="caption" color={pairingRuleIsHard(compatibilityDetail.pairingRule) ? 'warning.main' : 'text.secondary'} sx={{ display: 'block' }}>
                        {pairingRuleHelpText[compatibilityDetail.pairingRule]}
                      </Typography>
                    )}
                    {sameDayConflict && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        {sameDayConflictLabel(sameDayConflict)}
                      </Typography>
                    )}
                    {assignedLabel && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        {assignedLabel}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            }),
          ])}
        </Select>
      </FormControl>
    );
  };

  const linePartnerCompatibilityDetail = (position: LeagueConfigurationPositionRecord) => {
    if (position.required_player_count < 2) return null;
    const selectedEntries = lineEntries(position.id);
    if (selectedEntries.length < 2) return { compatibility: 'Unknown' as PartnerCompatibility, pairingRule: 'none' as PartnerPairingRule, notes: 'Assign both players to see compatibility.' };
    return partnerCompatibility(selectedEntries[0].player_id, selectedEntries[1].player_id);
  };

  const renderLineCard = (position: LeagueConfigurationPositionRecord) => {
    const lineStatus = lineStatusForPosition(position.id);
    const isDefaultedLine = defaultedLineStatuses.has(lineStatus);
    const playersForLine = linePlayers(position);
    const compatibilityDetail = linePartnerCompatibilityDetail(position);
    const warnings = lineWarningMessages(position);
    const lineHasEntries = entries.some((entry) => entry.league_configuration_position_id === position.id);
    const logistics = logisticsForPosition(position.id);
    const effectiveFacility = logistics.facilityId ? lookupLabel(lookups, 'facilities', logistics.facilityId) : '';
    const logisticsLabel = [
      logistics.date,
      logistics.warmupTime ? `Warmup ${formatTime(logistics.warmupTime, timeFormat, { match_date: logistics.date, timezone: match?.timezone })}` : '',
      logistics.startTime ? formatTime(logistics.startTime, timeFormat, { match_date: logistics.date, timezone: match?.timezone }) : '',
      effectiveFacility || logistics.locationText,
      logistics.courtAssignment ? `Court ${logistics.courtAssignment}` : '',
    ].filter(Boolean).join(' · ');
    const availabilityScheduleWarning = lineHasEntries && logistics.hasMaterialScheduleOverride;
    return (
      <Paper key={position.id} variant="outlined" sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '110px 180px minmax(0, 1fr) 44px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{lineDisplayName(position)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{lineEdrLabel(playersForLine, position.line_type)}</Typography>
            {compatibilityDetail && (
              <Tooltip title={`Partner Compatibility: ${compatibilityDetail.compatibility}${compatibilityDetail.notes ? `: ${compatibilityDetail.notes}` : ''}`}>
                <Chip
                  size="small"
                  color={compatibilitySummaryChipColor(compatibilityDetail.compatibility)}
                  label="Compatibility"
                  sx={{ mt: 0.75 }}
                />
              </Tooltip>
            )}
            {compatibilityDetail && compatibilityDetail.pairingRule !== 'none' && (
              <Tooltip title={pairingRuleHelpText[compatibilityDetail.pairingRule]}>
                <Chip
                  size="small"
                  color={pairingRuleChipColor(compatibilityDetail.pairingRule)}
                  label={pairingRuleLabels[compatibilityDetail.pairingRule]}
                  sx={{ mt: 0.75, ml: 0.75 }}
                />
              </Tooltip>
            )}
            <TextField
              label="Court"
              size="small"
              value={lineLogistics[position.id]?.court_assignment ?? logistics.courtAssignment ?? ''}
              placeholder="Court #"
              disabled={!canEdit || savingKey === `line-court:${position.id}`}
              onChange={(event) => setLineLogistics((current) => ({
                ...current,
                [position.id]: { ...current[position.id], court_assignment: event.target.value || null },
              }))}
              onBlur={(event) => void saveLineCourtAssignment(position.id, event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') (event.currentTarget as HTMLInputElement).blur();
              }}
              sx={{ mt: 1, width: '100%' }}
              inputProps={{ 'aria-label': `${position.position_code} court assignment` }}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Line Status</InputLabel>
            <Select
              label="Line Status"
              value={lineStatus}
              disabled={!canEdit || savingKey === `line-status:${position.id}`}
              onChange={(event) => void saveLineStatus(position, event.target.value as LineStatus)}
            >
              {lineStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack spacing={1}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                {logisticsLabel || 'Match schedule not set'}
              </Typography>
              {logistics.wasRescheduled && <Chip size="small" color="warning" label="Rescheduled" />}
              {logistics.hasOverride && !logistics.wasRescheduled && <Chip size="small" variant="outlined" label="Alternate schedule" />}
              <Button size="small" variant="text" startIcon={<EditIcon />} onClick={() => openLineLogistics(position)} disabled={!canEdit}>
                Change schedule
              </Button>
            </Stack>
            {availabilityScheduleWarning && (
              <Alert severity="warning" sx={{ py: 0.25 }}>
                Assigned player availability was collected for the original match schedule. Confirm this line’s updated date and time with the players.
              </Alert>
            )}
            <Box sx={workspacePlayerTileGridSx}>
              {lineSlots(position).map((slot) => {
                const key = slotKey(position.id, slot.slotNumber);
                const entry = entryBySlot.get(key);
                const selectedPlayer = entry ? playersById.get(entry.player_id) : undefined;
                return (
                  <WorkspaceSlot
                    key={key}
                    slot={slot}
                    player={selectedPlayer}
                    isMixed={isMixedDoublesTeam}
                    isDefaulted={isDefaultedLine}
                    canEdit={canEdit && lineupSaveState !== 'saving'}
                    ratingBasis={playerRatingBasis}
                    activePartnerContext={Boolean(selectedPlayer && resolvedPartnerContext?.playerId === selectedPlayer.id && resolvedPartnerContext.positionId === position.id)}
                    signals={selectedPlayer ? workspaceTileSignals.genericByPlayerId.get(selectedPlayer.id) : []}
                    draggingPlayer={activeDragPlayerId ? playersById.get(activeDragPlayerId) : undefined}
                    selectedPoolPlayerName={selectedPoolPlayerId ? playerName(playersById.get(selectedPoolPlayerId), userPreferences) : null}
                    onSelect={() => {
                      if (selectedPoolPlayerId) {
                        void saveSlot(slot, selectedPoolPlayerId);
                        setSelectedPoolPlayerId(null);
                      }
                      else if (selectedPlayer) togglePartnerContext(selectedPlayer.id, position);
                      else setSnack({ severity: 'info', message: 'Choose a player from the Player Pool, then choose this slot.' });
                    }}
                    onRemove={selectedPlayer ? () => persistWorkspaceMove(selectedPlayer.id) : undefined}
                  />
                );
              })}
            </Box>
            {warnings.length > 0 && (
              <Stack spacing={0.75}>
                {warnings.map((warning) => (
                  <Alert key={warning} severity={/Do Not Pair|unavailable|assigned more than once/i.test(warning) ? 'error' : 'warning'} sx={{ py: 0.25 }}>
                    {warning}
                  </Alert>
                ))}
              </Stack>
            )}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
            <Tooltip title="Line actions">
              <span>
                <IconButton
                  aria-label={`${position.position_code} line actions`}
                  disabled={!canEdit}
                  onClick={(event) => openLineMenu(event, position.id)}
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {isDefaultedLine && (
          <Chip size="small" color="warning" label={lineStatusLabel(lineStatus)} sx={{ mt: 1 }} />
        )}
        {!lineHasEntries && !isDefaultedLine && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            This line still needs {position.required_player_count === 1 ? 'a player' : 'players'}.
          </Typography>
        )}
      </Paper>
    );
  };

  if (!matchId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Open a match from Team Events to build a lineup.
        </Typography>
      </Paper>
    );
  }

  const lineupBreadcrumbItems = buildBreadcrumbTrail(lineupCanonicalContext(
    match ? `vs ${match.opponent_name || 'TBD'}` : 'Match Detail',
    match ? `/matches/${match.id}` : undefined,
    'Lineup Builder',
    breadcrumbOrigin,
  ));
  const handleBreadcrumbNavigate = (item: BreadcrumbItem) => {
    if (item.path === '/planning-board') {
      onBackToMatch(match?.id || matchId || '');
      return;
    }
    if (item.path === '/team-events') {
      onOpenTeamEvents?.();
      return;
    }
    if (match && item.path === `/matches/${match.id}`) {
      onOpenMatchDetail?.(match.id);
    }
  };

  return (
    <Stack spacing={2}>
      <DraftRecoveryDialog
        open={Boolean(lineupDraftPersistence.recoverableDraft)}
        updatedAt={lineupDraftPersistence.recoverableDraft?.updatedAt}
        onRestore={() => {
          const restored = lineupDraftPersistence.restoreDraft();
          if (!restored) return;
          setStandbyPlayerId(restored.standbyPlayerId);
          setStandbyNotes(restored.standbyNotes);
          setCancellationForm(restored.cancellationForm);
        }}
        onDiscard={lineupDraftPersistence.discardDraft}
      />
      <BreadcrumbTrail items={lineupBreadcrumbItems} onNavigate={handleBreadcrumbNavigate} />
      {match && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ContextBackButton
            destination={{
              label: breadcrumbOrigin === 'planning-board' ? 'Planning Board' : 'Match',
              page: breadcrumbOrigin === 'planning-board' ? 'planning-board' : 'match-detail',
              path: breadcrumbOrigin === 'planning-board' ? '/planning-board' : `/matches/${match.id}`,
              source: 'canonical-parent',
            }}
            onBack={() => onBackToMatch(match.id)}
          />
          {onOpenAvailability && <Button size="small" variant="outlined" onClick={() => onOpenAvailability(match.id, match.team_id)}>View Player Availability</Button>}
          {isWorkflowFullScreen && (
            <Button size="small" variant="text" startIcon={<FullscreenExitIcon />} onClick={onExitWorkflowFullScreen}>
              Exit Full Screen
            </Button>
          )}
        </Stack>
      )}
      <Dialog open={recommendationDialogOpen} onClose={() => setRecommendationDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Lineup Recommendations</DialogTitle>
        <DialogContent dividers>
          {!recommendationAnalysis ? (
            <Typography color="text.secondary">No recommendation analysis has been generated.</Typography>
          ) : recommendationAnalysis.unresolvedConstraints.length > 0 ? (
            <Stack spacing={1}>
              {recommendationAnalysis.unresolvedConstraints.map((constraint) => (
                <Alert key={constraint} severity="warning">{constraint}</Alert>
              ))}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                Recommendations are deterministic suggestions only. Review before applying; the engine will not publish the lineup.
              </Alert>
              {recommendationAnalysis.plans.map((plan) => {
                const changed = plan.changes.filter((change) => change.changeType !== 'unchanged');
                return (
                  <Paper key={plan.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{plan.name}</Typography>
                          {plan.recommended && <Chip size="small" color="success" label="Recommended" />}
                          <Chip size="small" label={`Score ${plan.overallScore}`} />
                          <Chip size="small" label={`${changed.length} change${changed.length === 1 ? '' : 's'}`} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Disruption penalty {plan.disruptionPenalty}. Strategy {recommendationAnalysis.strategy.name} v{recommendationAnalysis.strategy.version}.
                        </Typography>
                      </Box>
                      <Button
                        variant={plan.recommended ? 'contained' : 'outlined'}
                        disabled={!canEdit || savingKey === `recommendation:${plan.id}`}
                        onClick={() => void applyRecommendationPlan(plan)}
                      >
                        Apply Plan
                      </Button>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 1, mt: 2 }}>
                      {Object.entries(plan.objectiveScoreBreakdown).map(([key, value]) => (
                        <Paper key={key} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key.replace(/[A-Z]/g, (letter) => ` ${letter}`)}</Typography>
                          <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                        </Paper>
                      ))}
                    </Box>
                    <TableContainer sx={{ mt: 2, maxHeight: 260 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Line</TableCell>
                            <TableCell>Current</TableCell>
                            <TableCell>Proposed</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {changed.map((change) => (
                            <TableRow key={`${change.leagueConfigurationPositionId}:${change.slotNumber}`}>
                              <TableCell>{change.positionCode} Slot {change.slotNumber}</TableCell>
                              <TableCell>{change.oldPlayerId ? playerName(playersById.get(change.oldPlayerId), userPreferences) : '-'}</TableCell>
                              <TableCell>{change.newPlayerId ? playerName(playersById.get(change.newPlayerId), userPreferences) : '-'}</TableCell>
                              <TableCell>{change.reason}</TableCell>
                            </TableRow>
                          ))}
                          {changed.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>No changes needed.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack spacing={0.75} sx={{ mt: 2 }}>
                      {plan.explanations.map((explanation) => (
                        <Typography key={explanation} variant="body2" color="text.secondary">{explanation}</Typography>
                      ))}
                      {plan.ratingExplanations.slice(0, 6).map((explanation) => (
                        <Typography key={`${plan.id}:${explanation.playerId}:${explanation.assignmentType}`} variant="caption" color="text.secondary">
                          {playerName(playersById.get(explanation.playerId), userPreferences)}: {explanation.text}
                        </Typography>
                      ))}
                      {plan.warnings.map((warning) => <Alert key={warning} severity="warning">{warning}</Alert>)}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecommendationDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={correctionDialogOpen} onClose={() => setCorrectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Correct Completed Lineup</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This temporarily opens editing for this submitted match. Every change is audited. Enter a specific reason before continuing.
          </Typography>
          <TextField label="Correction reason" value={correctionReason} onChange={(event) => setCorrectionReason(event.target.value)}
            multiline minRows={3} fullWidth required inputProps={{ minLength: 10 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={correctionReason.trim().length < 10} onClick={() => void beginCorrection()}>Open Correction</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lineup History</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {historyRows.map((row, index) => (
              <Paper key={`${row.label}:${row.timestamp}:${index}`} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{row.label}</Typography>
                    {row.detail && <Typography variant="caption" color="text.secondary">{row.detail}</Typography>}
                    {row.actor && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        By {lookupLabel(lookups, 'app_users', row.actor)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{formatDateTime(row.timestamp)}</Typography>
                </Stack>
              </Paper>
            ))}
            {historyRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No lineup history has been recorded yet.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(lineLogisticsPositionId)} onClose={closeLineLogistics} maxWidth="sm" fullWidth>
        <DialogTitle>Line Schedule and Court</DialogTitle>
        <DialogContent dividers>
          {lineLogisticsPositionId && (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Leave a field blank to inherit the match schedule. The match remains the default unless this line needs its own details.
              </Typography>
              <TextField label="Date override" type="date" value={lineLogisticsDraft.scheduled_date_override || ''} onChange={(event) => setLineLogisticsDraft((current) => ({ ...current, scheduled_date_override: event.target.value || null }))} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Warmup time override" type="time" value={lineLogisticsDraft.warmup_time_override || ''} onChange={(event) => setLineLogisticsDraft((current) => ({ ...current, warmup_time_override: event.target.value || null }))} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Start time override" type="time" value={lineLogisticsDraft.start_time_override || ''} onChange={(event) => setLineLogisticsDraft((current) => ({ ...current, start_time_override: event.target.value || null }))} InputLabelProps={{ shrink: true }} fullWidth />
              <FormControl fullWidth>
                <InputLabel>Facility override</InputLabel>
                <Select label="Facility override" value={lineLogisticsDraft.facility_id_override || ''} onChange={(event) => setLineLogisticsDraft((current) => ({ ...current, facility_id_override: String(event.target.value) || null }))}>
                  <MenuItem value=""><em>Use match facility</em></MenuItem>
                  {lookups.facilities.map((facility) => <MenuItem key={facility.value} value={facility.value}>{facility.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Location text override" value={lineLogisticsDraft.location_text_override || ''} onChange={(event) => setLineLogisticsDraft((current) => ({ ...current, location_text_override: event.target.value || null }))} helperText="Use for a location that is not a saved facility." fullWidth />
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {(['scheduled_date_override', 'warmup_time_override', 'start_time_override', 'facility_id_override', 'location_text_override'] as const).map((field) => (
                  <Button key={field} size="small" onClick={() => setLineLogisticsDraft((current) => ({ ...current, [field]: null }))}>Reset {field === 'scheduled_date_override' ? 'date' : field === 'warmup_time_override' ? 'warmup' : field === 'start_time_override' ? 'start' : field === 'facility_id_override' ? 'facility' : 'location'}</Button>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={closeLineLogistics}>Cancel</Button><Button variant="contained" onClick={() => void saveLineLogistics()} disabled={!canEdit || savingKey?.startsWith('line-logistics:')}>Save Line Schedule</Button></DialogActions>
      </Dialog>
      <Dialog open={assignCourtsOpen} onClose={() => setAssignCourtsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Courts</DialogTitle>
        <DialogContent dividers><TextField autoFocus fullWidth label="Starting court number" type="number" inputProps={{ min: 1, step: 1 }} value={courtStartNumber} onChange={(event) => setCourtStartNumber(event.target.value)} helperText="Courts will be assigned in lineup order." /></DialogContent>
        <DialogActions><Button onClick={() => setAssignCourtsOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => void assignCourts()} disabled={savingKey === 'assign-courts'}>Assign Courts</Button></DialogActions>
      </Dialog>
      <Popover
        open={lineupHealthOpen && !isMobileHealthOverlay}
        anchorEl={lineupHealthAnchor}
        onClose={closeLineupHealth}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 420, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 120px)', overflow: 'auto', p: 2 },
          },
        }}
      >
        {lineupHealthDetails}
      </Popover>
      <Dialog
        open={lineupHealthOpen && isMobileHealthOverlay}
        onClose={closeLineupHealth}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { m: 0, position: 'fixed', bottom: 0, width: '100%', maxHeight: '82vh', borderRadius: '12px 12px 0 0' } }}
      >
        <DialogTitle>Lineup Health</DialogTitle>
        <DialogContent dividers>
          {lineupHealthDetails}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineupHealth}>Close</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1.25 }}>
        <Box sx={{ borderLeft: match ? `6px solid ${lookupTeamAccentColor(lookups, match.team_id)}` : undefined, pl: match ? 1.5 : 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
            Match Lineup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMatch(match, lookups, timeFormat) || 'Loading match...'}
          </Typography>
          <DraftSavedIndicator savedAt={lineupDraftPersistence.draftSavedAt} />
        </Box>
        {match && (
          <Stack direction="row" spacing={1} alignItems="center">
            {splitSchedule && <Chip size="small" color="warning" label="Split Schedule" />}
            {match.match_status === 'Completed' && isOwner && !readOnly && (
              correctionSessionId
                ? <Button size="small" variant="outlined" color="warning" onClick={() => void endCorrection()}>Finish Correction</Button>
                : <Button size="small" variant="outlined" onClick={() => setCorrectionDialogOpen(true)}>Correct Completed Lineup</Button>
            )}
            <Button size="small" variant="outlined" onClick={() => setAssignCourtsOpen(true)} disabled={!canEdit}>Assign Courts</Button>
          </Stack>
        )}
      </Box>

      {loading && <LinearProgress />}

      {match && (
        <Paper variant="outlined" sx={{ px: 1.75, py: 1.1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={isPublished ? <CheckCircleOutlineIcon /> : undefined}
                color={statusChipColor}
                label={statusChipLabel}
                aria-label={`Lineup status ${statusChipLabel}`}
                sx={{ fontWeight: 800 }}
              />
              {hasValidationIssues && <Chip color="warning" label="Validation Issues" aria-label="Lineup has validation issues" />}
              {recommendationAnalysis && <Chip color="info" label="Recommendation Pending" aria-label="Recommendation pending" />}
              {match.lineup_published_at && (
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>Published</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDateTime(match.lineup_published_at)}</Typography>
                </Box>
              )}
              {lineupHealthPanel}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'stretch', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
              {isPublished && (
                <Tooltip title={moveToDraftDisabledReason || 'Move this lineup back to Draft'}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      disabled={!canMoveToDraft}
                      onClick={() => void moveLineupToDraft()}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Move to Draft
                    </Button>
                  </span>
                </Tooltip>
              )}

              {showPublishAction && (
                <Tooltip title={publishDisabledReason || 'Publish lineup'}>
                  <span>
                    <ButtonGroup variant="outlined" disabled={!canPublish} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <Button startIcon={<PublishIcon />} onClick={() => void publishLineup()} sx={{ flex: { xs: 1, sm: 'initial' } }}>
                        Publish Only
                      </Button>
                      <Button
                        aria-label="Publish options"
                        aria-controls={publishMenuAnchor ? 'lineup-publish-menu' : undefined}
                        aria-expanded={publishMenuAnchor ? 'true' : undefined}
                        aria-haspopup="menu"
                        onClick={(event) => setPublishMenuAnchor(event.currentTarget)}
                      >
                        <ExpandMoreIcon />
                      </Button>
                    </ButtonGroup>
                  </span>
                </Tooltip>
              )}

              {match && !isCompleted && (match.lineup_status === 'Draft' || isPublished) && (
                <GroupMeLineupSend
                  matchId={match.id}
                  disabled={!auditUserId || hasValidationIssues || loading}
                  disabledReason={publishDisabledReason || 'Complete lineup validation before sending.'}
                  onSent={async (message) => {
                    lineupDraftPersistence.clearDraft();
                    await loadLineup();
                    setSnack({ severity: 'success', message });
                  }}
                />
              )}

              <Tooltip title="Lineup actions">
                <IconButton
                  aria-label="Lineup actions"
                  aria-controls={actionMenuAnchor ? 'lineup-action-menu' : undefined}
                  aria-expanded={actionMenuAnchor ? 'true' : undefined}
                  aria-haspopup="menu"
                  onClick={(event) => setActionMenuAnchor(event.currentTarget)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      )}

      {(pairingRuleValidation.hardErrors.length > 0 || pairingRuleValidation.warnings.length > 0) && (
        <Stack spacing={0.75}>
          {pairingRuleValidation.hardErrors.map((message) => (
            <Alert key={message} severity="error">{message}</Alert>
          ))}
          {pairingRuleValidation.hardErrors.length > 0 && canEdit && (
            <Box>
              <Button variant="outlined" color="warning" onClick={() => void overridePairingRulesForMatch()}>
                Override Scheduling Preferences for This Match
              </Button>
            </Box>
          )}
          {pairingRuleValidation.warnings.map((message) => (
            <Alert key={message} severity="warning">{message}</Alert>
          ))}
        </Stack>
      )}

      <Menu id="lineup-publish-menu" anchorEl={publishMenuAnchor} open={Boolean(publishMenuAnchor)} onClose={() => setPublishMenuAnchor(null)}>
        <MenuItem
          disabled={!canPublish}
          onClick={() => {
            setPublishMenuAnchor(null);
            void publishLineup();
          }}
        >
          <PublishIcon fontSize="small" sx={{ mr: 1 }} />
          Publish Only
        </MenuItem>
        <MenuItem
          disabled={!canPublish}
          onClick={() => {
            setPublishMenuAnchor(null);
            void publishLineup({ copyInternal: true });
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Publish and Copy Lineup
        </MenuItem>
        <MenuItem disabled>
          <PublishIcon fontSize="small" sx={{ mr: 1 }} />
          Publish and Notify Team
        </MenuItem>
      </Menu>

      <Menu id="lineup-action-menu" anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={() => setActionMenuAnchor(null)}>
        {showSuggestAction && (
          <MenuItem
            disabled={!canSuggest}
            onClick={() => {
              setActionMenuAnchor(null);
              openRecommendationDialog();
            }}
          >
            <AutoFixHighIcon fontSize="small" sx={{ mr: 1 }} />
            Suggest Lineup
          </MenuItem>
        )}
        {isPublished && (
          <MenuItem
            disabled={!canMoveToDraft}
            onClick={() => {
              setActionMenuAnchor(null);
              void moveLineupToDraft();
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Move to Draft
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setActionMenuAnchor(null);
            void copyLineup(false);
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copy Lineup
        </MenuItem>
        <MenuItem
          disabled={!canGenerateTcdScoresheet || scoresheetGenerating}
          title={scoresheetDisabledReason || 'Download the official TCD Mixed scoresheet PDF from the saved lineup'}
          onClick={() => {
            setActionMenuAnchor(null);
            void generateTcdScoresheet('download');
          }}
        >
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
          Download TCD Scoresheet PDF
        </MenuItem>
        <MenuItem
          disabled={!canEdit || entries.length === 0 || savingKey === 'clear-lineup'}
          onClick={() => {
            setActionMenuAnchor(null);
            void clearEntireLineup();
          }}
          sx={{ color: 'error.main' }}
        >
          <RemoveIcon fontSize="small" sx={{ mr: 1 }} />
          Clear Lineup
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionMenuAnchor(null);
            setRecommendationAnalysis(null);
            setSnack({ severity: 'success', message: 'Recommendation reset.' });
          }}
        >
          <RestartAltIcon fontSize="small" sx={{ mr: 1 }} />
          Reset Recommendation
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionMenuAnchor(null);
            setHistoryDialogOpen(true);
          }}
        >
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          View History
        </MenuItem>
      </Menu>

      {isCompleted && <Alert severity={correctionSessionId ? 'warning' : 'info'}>
        {correctionSessionId ? 'Audited correction is open. All lineup changes are recorded with your reason.' : 'Submitted match lineups are read-only. Owner/Admin correction requires a reason.'}
      </Alert>}
      {incompletePositions.length > 0 && (
        <Alert severity="warning">
          Incomplete lineup: {incompletePositions.map((position) => position.position_code).join(', ')}
        </Alert>
      )}
      {genderWarnings.map((warning) => (
        <Alert key={warning} severity="error">
          {warning}
        </Alert>
      ))}
      {duplicateWarnings.map((warning) => (
        <Alert key={warning} severity="error">
          {warning}
        </Alert>
      ))}
      {standbyLineupOverlapWarnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}

      <Paper variant="outlined" sx={{ px: 1.75, py: 1.4 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'action.hover', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <GroupsIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                Lineup Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(120px, 1fr))' }, gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Avg EDR (All Players)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                    {formatEdr(lineupEdrSummary.overall)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lineupEdrSummary.assignedWithEdr} of {assignedLineupEdrPlayers.length} players rated
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Opponent Avg EDR</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 800 }}>
                    {formatEdr(lineupEdrSummary.opponent)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Difference</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 800,
                      color: lineupEdrSummary.difference === null ? 'text.primary' : lineupEdrSummary.difference > 0 ? 'success.main' : lineupEdrSummary.difference < 0 ? 'error.main' : 'text.primary',
                    }}
                  >
                    {formatEdrDifference(lineupEdrSummary.difference)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>

        </Stack>
      </Paper>

      <DndContext sensors={sensors} collisionDetection={workspaceCollisionDetection} onDragStart={handleWorkspaceDragStart} onDragEnd={handleWorkspaceDragEnd} onDragCancel={() => setActiveDragPlayerId(null)}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(380px, 0.9fr)' }, gap: 2, alignItems: 'start' }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.75 } }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Lineup Slots</Typography>
              <Typography variant="caption" color={lineupSaveState === 'error' ? 'error.main' : 'text.secondary'}>
                {lineupSaveState === 'saving' ? 'Saving…' : lineupSaveState === 'error' ? 'Save issue — check lineup' : 'All changes saved'}
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {positions.map((position) => renderLineCard(position))}
              {positions.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No active lineup positions are configured for this match.</Typography>
              )}
            </Stack>
          </Paper>
          <WorkspacePoolDropZone canEdit={canEdit}>
            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.75 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Player Pool</Typography>
                <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(userPreferences.groupPlayersBySex)}
                        onChange={(event) => onUserPreferencesChange({ groupPlayersBySex: event.target.checked })}
                      />
                    )}
                    label="Group by Gender"
                    sx={{ m: 0, mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', whiteSpace: 'nowrap' } }}
                  />
                  <Tooltip title="Planning signals">
                    <IconButton size="small" aria-label="Open planning signals key" onClick={(event) => setPlanningSignalsAnchor(event.currentTarget)}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {selectedPoolPlayerId && <Button size="small" onClick={() => setSelectedPoolPlayerId(null)}>Clear selection</Button>}
                </Stack>
              </Stack>
              {resolvedPartnerContext && (
                <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 800, mb: 0.5 }}>
                  Partner suggestions for {resolvedPartnerContext.player.first_name} · {resolvedPartnerContext.position.position_code}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Drag a player to a slot, or choose a player and then choose a slot. Drag an assigned player here, or use its remove action, to return it to the pool.
              </Typography>
              {lineupWorkspacePoolPlayers.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No unassigned roster players.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {lineupWorkspacePoolGroups.map((group) => (
                    <Box key={group.key}>
                      {group.label && (
                        <Typography
                          variant="overline"
                          component="h3"
                          sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 800, lineHeight: 1.5 }}
                        >
                          {group.label} ({group.players.length})
                        </Typography>
                      )}
                      <Box sx={workspacePlayerTileGridSx}>
                        {group.players.map((player) => (
                          <DraggableWorkspacePlayerTile
                            key={player.id}
                            dragId={`pool-player:${player.id}`}
                            playerId={player.id}
                            player={player}
                            ratingBasis={playerRatingBasis}
                            signals={workspaceTileSignals.poolByPlayerId.get(player.id) || []}
                            selected={selectedPoolPlayerId === player.id}
                            disabled={!canEdit || lineupSaveState === 'saving'}
                            onClick={() => setSelectedPoolPlayerId((current) => current === player.id ? null : player.id)}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </WorkspacePoolDropZone>
        </Box>
        <DragOverlay dropAnimation={null}>
          {activeDragPlayerId && playersById.get(activeDragPlayerId) ? (
            <Box sx={{ width: workspacePlayerTileWidth, maxWidth: 'calc(100vw - 32px)' }}><WorkspacePlayerTile player={playersById.get(activeDragPlayerId)!} ratingBasis={playerRatingBasis} signals={workspaceTileSignals.genericByPlayerId.get(activeDragPlayerId) || []} /></Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Popover
        open={Boolean(planningSignalsAnchor)}
        anchorEl={planningSignalsAnchor}
        onClose={() => setPlanningSignalsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 340 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>Planning Signals</Typography>
          <Typography variant="caption" component="div"><GroupsIcon fontSize="inherit" /> Preferred partner · <HandshakeIcon fontSize="inherit" /> Compatible partner · <BlockIcon fontSize="inherit" /> Don’t partner</Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}><TrackChangesIcon fontSize="inherit" /> Target opportunity · <EventBusyIcon fontSize="inherit" /> Participation · <SwapHorizIcon fontSize="inherit" /> Workload · <WarningAmberIcon fontSize="inherit" /> Maybe assignment</Typography>
        </Box>
      </Popover>

      <Menu
        anchorEl={lineMenuAnchor}
        open={Boolean(lineMenuAnchor && activeLineMenuPosition)}
        onClose={closeLineMenu}
      >
        {activeLineMenuPosition && activeLineMenuPosition.required_player_count > 1 && (
          <MenuItem
            disabled={!canEdit || savingKey !== null}
            onClick={() => {
              const position = activeLineMenuPosition;
              closeLineMenu();
              void swapLinePlayers(position);
            }}
          >
            <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
            Swap Players
          </MenuItem>
        )}
        {activeLineMenuPosition && (
          <MenuItem
            disabled={!canEdit || savingKey === `clear-line:${activeLineMenuPosition.id}`}
            onClick={() => {
              const position = activeLineMenuPosition;
              closeLineMenu();
              void clearLine(position);
            }}
          >
            <RemoveIcon fontSize="small" sx={{ mr: 1 }} />
            Clear Line
          </MenuItem>
        )}
        {activeLineMenuPosition && canRecordCancellation && lineEntries(activeLineMenuPosition.id).map((entry) => (
          <MenuItem
            key={`cancel:${entry.id}`}
            disabled={savingKey === `cancel:${entry.id}`}
            onClick={() => {
              closeLineMenu();
              openCancellationDialog(entry);
            }}
          >
            <WarningAmberIcon fontSize="small" sx={{ mr: 1 }} />
            Record Cancellation: Player {entry.slot_number}
          </MenuItem>
        ))}
        {activeLineMenuPosition && !canRecordCancellation && match?.lineup_status === 'Draft' && lineEntries(activeLineMenuPosition.id).length > 0 && (
          <MenuItem disabled title="Cancellation tracking is available after the lineup is published.">
            <WarningAmberIcon fontSize="small" sx={{ mr: 1 }} />
            Record Cancellation
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            closeLineMenu();
            setRecommendationDialogOpen(true);
          }}
        >
          <RestartAltIcon fontSize="small" sx={{ mr: 1 }} />
          Restore Recommendation
        </MenuItem>
      </Menu>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Standby Players
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 1fr) minmax(240px, 1fr) auto' }, gap: 2, alignItems: 'center' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Standby Player</InputLabel>
            <Select label="Standby Player" value={standbyPlayerId} disabled={!canEdit} onChange={(event) => setStandbyPlayerId(event.target.value)}>
              <MenuItem value="">Select player</MenuItem>
              {groupedStandbyPlayers.flatMap((group) => [
                <ListSubheader key={`standby-tier-${group.tier || 'none'}`}>{group.label}</ListSubheader>,
                ...group.players.map((player) => {
                  const assignedLabel = assignedLabelForPlayer(player.id);
                  const sameDayConflict = sameDayConflictForPlayer(player.id);
                  return (
                    <MenuItem key={player.id} value={player.id}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2">{playerDisplay(player, userPreferences)}</Typography>
                          {isDesignatedSubstitute(player) && <Chip size="small" variant="outlined" color="info" label="Substitute" />}
                          {sameDayConflict && (
                            <Tooltip title={sameDayConflictLabel(sameDayConflict)}>
                              <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {playerWorkloadSummary(player)}
                        </Typography>
                        {sameDayConflict && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                            {sameDayConflictLabel(sameDayConflict)}
                          </Typography>
                        )}
                        {assignedLabel && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                            {assignedLabel}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                }),
              ])}
            </Select>
          </FormControl>
          <TextField size="small" label="Notes" value={standbyNotes} disabled={!canEdit} onChange={(event) => setStandbyNotes(event.target.value)} />
          <Button variant="contained" disabled={!canEdit || !standbyPlayerId} onClick={() => void addStandby()}>
            Add Standby
          </Button>
        </Box>
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {standbyPlayers.map((standby) => (
                <TableRow key={standby.id} hover>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">{playerName(playersById.get(standby.player_id), userPreferences)}</Typography>
                      {assignedLabelForPlayer(standby.player_id) && (
                        <Chip size="small" color="warning" label="Also in official lineup" sx={{ alignSelf: 'flex-start' }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{standby.notes || '-'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" startIcon={<RemoveIcon />} disabled={!canEdit} onClick={() => void removeStandby(standby)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {standbyPlayers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No standby players added.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>


      <Dialog open={cancellationDialogOpen} onClose={() => setCancellationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Player Cancelled / Replace Player</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Player"
              value={selectedCancellationPlayer ? playerName(selectedCancellationPlayer, userPreferences) : ''}
              fullWidth
              disabled
            />
            <TextField
              label="Line"
              value={selectedCancellationEntry && selectedCancellationPosition ? `${selectedCancellationPosition.position_code} Slot ${selectedCancellationEntry.slot_number}` : ''}
              fullWidth
              disabled
            />
            <FormControl fullWidth>
              <InputLabel>Cancellation Type</InputLabel>
              <Select
                label="Cancellation Type"
                value={cancellationForm.cancellationType}
                onChange={(event) =>
                  setCancellationForm((current) => ({
                    ...current,
                    cancellationType: event.target.value as ParticipationCancellationType,
                  }))
                }
              >
                {participationCancellationTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cancellationForm.cancellationCountsTowardAllocation}
                  onChange={(event) => setCancellationForm((current) => ({
                    ...current,
                    cancellationCountsTowardAllocation: event.target.checked,
                    manualAllocationOverride: event.target.checked,
                  }))}
                />
              }
              label={`Count this match toward ${selectedCancellationPlayer ? playerName(selectedCancellationPlayer, userPreferences) : 'the cancelling player'}'s match target`}
            />
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                label="Reason"
                value={cancellationForm.reason}
                onChange={(event) => setCancellationForm((current) => ({ ...current, reason: event.target.value as ParticipationEventReason }))}
              >
                {participationEventReasonOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Replacement Player</InputLabel>
              <Select
                label="Replacement Player"
                value={cancellationForm.replacementPlayerId}
                onChange={(event) => setCancellationForm((current) => ({
                  ...current,
                  replacementPlayerId: event.target.value,
                  replacementAllocationOverride: event.target.value && event.target.value === current.replacementPlayerId ? current.replacementAllocationOverride : false,
                }))}
              >
                <MenuItem value="">No replacement yet</MenuItem>
                {groupedRosterPlayers(playersWithParticipationMetrics.filter((player) => player.id !== selectedCancellationEntry?.player_id), userPreferences).flatMap((group) => [
                  <ListSubheader key={`replacement-tier-${group.tier || 'none'}`}>{group.label}</ListSubheader>,
                  ...group.players.map((player) => {
                    const assignedLabel = assignedLabelForPlayer(player.id, selectedCancellationEntry?.id);
                    return (
                      <MenuItem key={player.id} value={player.id}>
                        <Box>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2">{playerDisplay(player, userPreferences)}</Typography>
                            {isDesignatedSubstitute(player) && <Chip size="small" variant="outlined" color="info" label="Substitute" />}
                          </Stack>
                          {assignedLabel && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                              {assignedLabel}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  }),
                ])}
              </Select>
            </FormControl>
            {cancellationForm.replacementPlayerId && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cancellationForm.replacementAllocationOverride}
                    onChange={(event) => setCancellationForm((current) => ({ ...current, replacementAllocationOverride: event.target.checked }))}
                  />
                }
                label={`Count this match toward ${playerName(playersById.get(cancellationForm.replacementPlayerId), userPreferences) || 'the replacement player'}'s match target`}
              />
            )}
            <TextField
              label="Notes"
              value={cancellationForm.notes}
              onChange={(event) => setCancellationForm((current) => ({ ...current, notes: event.target.value }))}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancellationDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => void submitCancellation()}>
            Save Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
